import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import Select from "react-select";
import JSZip from "jszip";
import FileSaver from "file-saver";
import moment from "moment-timezone";
import MaterialTable from "material-table";
import { Col, Row, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import pdfMake from "pdfmake/build/pdfmake.js";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import fonts_config from "./fonts_config.json";
import { Help } from "@material-ui/icons";

import { dirname } from "../../../util/resourcePath";
import { isMakroSite, getMakroInvoiceNo } from "../../../util/siteFunctions";
import { useFormatter } from "../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import usePeakLabels from "../../../util/peakLabels";
import Axios from "axios";

import ApprovalContent from "./ApprovalContent";
import EmailConfirm from "./EmailConfirm";
import InvoicePreview from "./InvoicePreview";
import LoaderGraph from "../../general/LoaderGraph";
import SummaryInvoiceTemplate from "./SummaryInvoiceTemplate";
import SummaryInvoiceCFW from "./SummaryInvoiceCFW";
import {
	localTimeToPickerTime,
	pickerTimeTolocalTime,
} from "../../../util/momentTimezone";
import {
	allEntityOpt,
	buildInvoiceReceiptNumber,
	formatOldEntityId,
	oldEntityFilter,
} from "../../../util/invoice";
import {
	isAcctUser,
	isClientHQUser,
	isITUser,
	isManagementUser,
	isOMUser,
	isGYTHUser,
	isClientTHUser,
	isMakroClientUser,
	isCFWClientUser,
	isGYUser,
	isAbleToSendEmailTH,
	isGYVNUser,
} from "../../../util/roles";
import { Popover, Spin, message } from "antd";
import { useUserProps } from "../../../util/userDBContext";
import ExtraLineForm from "./ExtraLineForm";
import "../../admin/IssueListComps/issueFormCss.css";
import "../admin.css";

import rfdc from "rfdc";
import _, { isArray } from "lodash";
import dayjs from "dayjs";
const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

const solarCalc = useSolarCalc();

function InvoiceTable(props) {
	window.JSZip = JSZip;
	const user = useUser();
	const userInfo = props.userInfo;
	const { loggedCountry } = useUserProps();

	const [formatNumber] = useFormatter();

	const directory = dirname();

	const [previewIsHidden, setPreviewIsHidden] = useState(true);
	const [formIsHidden, setFormIsHidden] = useState(true);
	const [invoiceSites, setInvoiceSites] = useState([]);
	const [columns, setColumns] = useState([]);
	const [isTechReport, setIsTechReport] = useState(false);
	const [showInputForm, setShowInputForm] = useState(false);
	const [approvalIsExpired, setApprovalIsExpired] = useState(true);
	const [previewInvoice, setPreviewInvoice] = useState(null);
	const [selectedMonth, setSelectedMonth] = useState(props.selectedMonth);
	const [selectedSite, setSelectedSite] = useState(null);
	const [showGraph, setShowGraph] = useState("");
	const [showRemark, setShowRemark] = useState("");
	const [position, setPosition] = useState(null);
	const [process, setContext] = useState("");
	const [country, setCountry] = useState(loggedCountry);
	const peakLabels = usePeakLabels(country);
	const calc = useSolarCalc();

	// ----------------------- Credit Note ----------------------
	const [isCreditNote, setIsCreditNote] = useState(false);
	const showCreditNote = useMemo(
		() => selectedMonth.format("YYYY-MM") === "2023-09",
		[selectedMonth],
	);
	const isCreditNoteSite = useCallback(
		(site) =>
			site.country === "TH" &&
			site.operation_status === 3 &&
			site.client_id !== 18 &&
			site.client_id !== 69 &&
			!/^demo_/g.test(site.table_name),
		[],
	);
	// ----------------------- End Credit Note ------------------

	const showVatInvoiceForm = useMemo(
		() =>
			previewInvoice?.approvalStatus === 2 &&
			selectedSite?.value?.country === "VN",
		[previewInvoice, selectedSite],
	);

	const engYear = parseInt(selectedMonth.format("YYYY"));
	const thYear = engYear + 543;

	const roles = user.roles;

	const [savedToSend, setSavedToSend] = useState(false);
	const [emailConfirmShown, setEmailConfirmShown] = useState(false);
	const [emailsRecipients, setEmailsRecipients] = useState([]);
	const [existingEmailLog, setExistingEmailLog] = useState(null);
	const [reSaveRecipients, setReSaveRecipients] = useState(false);
	const emailsSite = useRef(null);
	// ----------------------- Entity Filter ----------------------
	const [entityOpts, setEntityOpts] = useState(null);
	const [selectedEntity, setSelectedEntity] = useState(allEntityOpt);
	const matTbRef = useRef(null); // for resetting material table component
	// clear selected rows when country or entity select changes
	useEffect(() => {
		setSelectedRows([]);
		if (matTbRef && matTbRef.current) {
			matTbRef.current.onAllSelected(false);
		}
	}, [country, selectedEntity]);

	useEffect(() => {
		if (country === "VN" && !entityOpts) {
			GYAxios.get("/select/entities/VN").then((res) => {
				const entities = res.data.filter(oldEntityFilter);
				setEntityOpts([allEntityOpt, ...entities]);
			});
		}
	}, [country, entityOpts]);

	useEffect(() => {
		if (country == "TH") {
			GYAxios.get(
				"/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
			).then((res) => {
				if (res.status == 200) {
					setExistingEmailLog(res.data);
				} else {
					setExistingEmailLog(null);
					Noti.create({
						type: "e",
						msg: "Failed to get Email log - Button for emailing cannot be used!",
					});
				}
			});
		}
	}, [country, selectedMonth]);

	const invoicesFilter = useCallback(
		(invoices) => {
			return invoices.filter((invoice) => {
				if (country !== "VN") return true;
				if (selectedEntity.value === "_ALL") return true;
				// only for VN invoice and when entity is selected
				return formatOldEntityId(invoice.entity_id) === selectedEntity.value;
			});
		},
		[country, selectedEntity],
	);

	// ----------------------- Bulk actions -----------------------
	const userInvSameCountry = useMemo(
		() => country === loggedCountry,
		[country, loggedCountry],
	);
	const isShownBulkActions = useMemo(
		() =>
			(isClientTHUser(roles) && isClientHQUser(userInfo)) ||
			(isGYVNUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
			isITUser(roles),
		[roles, userInfo, userInvSameCountry],
	);
	const isShownCombinedDownload = useMemo(
		() =>
			country === "TH" &&
			((isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry],
	); // hide combined download button from client HQ TH and VN ACCT
	const isShownSeparateDownload = useMemo(
		() =>
			country === "TH" &&
			((isClientTHUser(roles) && isClientHQUser(userInfo)) ||
				(isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry, userInfo],
	); // hide separated download button from VN ACCT
	const isShownClientGroupedDownload = useMemo(
		() =>
			country === "VN" &&
			((isGYVNUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry],
	); // hide separated download button from TH users
	const [invoicePreviewForBulkActions, setInvoicePreviewForBulkActions] =
		useState({});
	const [
		makroInvoicePreviewForBulkActions,
		setMakroInvoicePreviewForBulkActions,
	] = useState({});
	const [
		CFWInvoicePreviewForBulkActions,
		setCFWInvoicePreviewForBulkActions,
	] = useState({});
	const docDefinitions = useRef([]); // array of PDFMake doc definitions
	const docsToDownload = useRef([]); // sites to download PDF in a queue
	const [isBulkDownloading, setIsBulkDownloading] = useState(false); // flag to show download progress
	const [bulkDownloadProgress, setBulkDownloadProgress] = useState(null); // show download progress text
	const [bulkDownloadProgressWarning, setBulkDownloadProgressWarning] =
		useState(null); // show download progress warning if the download is too slow
	const clickedBulkAction = useRef(null); // flag to check which bulk action is clicked, COMBINED or SEPARATED
	const BULK_ACTIONS = useMemo(
		() => ({
			COMBINED: "COMBINED",
			SEPARATED: "SEPARATED",
			CLIENT_GROUPED: "CLIENT_GROUPED",
		}),
		[],
	); // bulk action types
	const BULK_DOC_TYPES = useMemo(
		() => ({
			INVOICE: "INVOICE",
			MAKRO_INVOICE: "MAKRO_INVOICE",
			CFW_INVOICE: "CFW_INVOICE",
			BILLING_DETAIL: "BILLING_DETAIL",
			TECH_REPORT: "TECH_REPORT",
		}),
		[],
	);
	const INVOICE_COPY_TYPES = useMemo(
		() => ({ ORIGINAL: "ORIGINAL", COPY: "COPY" }),
		[],
	);
	const onBeforeUnload = useCallback(
		() => !!bulkDownloadProgress || undefined,
		[bulkDownloadProgress],
	);
	// get selected sites from material table
	const [selectedRows, setSelectedRows] = useState([]);
	const makroClientId = useMemo(() => 18, []);
	const CFWClientId = useMemo(() => 299, []);
	const makroTestSites = useMemo(() => ["makro_ladkrabang", "makro_ram24"], []);
	const getSelectedSites = () =>
		selectedRows.filter(
			(invoice) =>
				(invoice.country === "TH" || invoice.country === "VN") &&
				((invoice.country === "TH" && invoice.approvalStatus === 3) ||
					invoice.country === "VN") &&
				!makroTestSites.includes(invoice.table_name) &&
				invoice.table_name !== "demo_thailand" &&
				invoice.table_name !== "demo_vietnam" &&
				invoice.tableData.checked,
		);
	const selectedMonthStr = useMemo(
		() => props.selectedMonth.format("YYYY-MM"),
		[props.selectedMonth],
	);
	// reset bulk download states
	const resetBulkDownloadStates = () => {
		setInvoicePreviewForBulkActions({});
		setMakroInvoicePreviewForBulkActions({});
		setCFWInvoicePreviewForBulkActions({});
		docDefinitions.current = [];
		setIsBulkDownloading(false);
		setBulkDownloadProgress(null);
		clickedBulkAction.current = null;
		setSavedToSend(false);
		setReSaveRecipients(false);
		setEmailConfirmShown(false);
		setEmailsRecipients([]);
		emailsSite.current = null;
	};
	// create COMBINED PDF
	const createCombinedPDF = async () => {
		const uniqueSiteIDs = [...new Set(docDefinitions.current.map(item => item.selectedSite?.value?.siteID).filter(Boolean))];
		let mailBillAddMap = {};
		try {
			const res = await GYAxios.post('/invoice/billing_addresses', { siteIDs: uniqueSiteIDs });
			for (const siteID of uniqueSiteIDs) {
				mailBillAddMap[siteID] = res.data[siteID]?.mail_bill_add?.split("\r\n")[1] || null;
			}
		} catch (e) {
			mailBillAddMap = {};
		}

		const sorted = [...docDefinitions.current].sort((a, b) => {
			const siteIdA = a.selectedSite?.value?.siteID;
			const siteIdB = b.selectedSite?.value?.siteID;
			const addrA = mailBillAddMap[siteIdA] || a.selectedSite?.value?.billingAddress || '';
			const addrB = mailBillAddMap[siteIdB] || b.selectedSite?.value?.billingAddress || '';
			if (addrA !== addrB) return addrA.localeCompare(addrB);
			const companyA = a.selectedSite?.value?.company || '';
			const companyB = b.selectedSite?.value?.company || '';
			return companyA.localeCompare(companyB);
		});

		let newDefinition = { ...clone(sorted[0].docDefinition), content: [], styles: {} };
		sorted.forEach(({ docType, selectedSite, docDefinition }) => {
			if (newDefinition.content.length) {
				newDefinition.content.push({ text: '', pageBreak: 'after' });
			}
			newDefinition.styles = { ...newDefinition.styles, ...docDefinition.styles }
			newDefinition.content.push(...docDefinition.content);
		});
		newDefinition = clone(newDefinition);
		return pdfMake.createPdf(newDefinition);
	};
	// create SEPARATED PDF by site: This will create 2 PDFs for each site, one for invoice and one for tech report
	const createSeparatedPDFsBySite = () => {
		const separatedPDFs = [];
		for (let {
			docType,
			invoiceCopyType,
			selectedSite,
			docDefinition,
			summaryPhase,
		} of docDefinitions.current) {
			if (
				docType === BULK_DOC_TYPES.INVOICE ||
				docType === BULK_DOC_TYPES.BILLING_DETAIL ||
				docType === BULK_DOC_TYPES.TECH_REPORT
			) {
				const pdfMakeInstance = pdfMake.createPdf(clone(docDefinition));
				separatedPDFs.push({
					pdfMakeInstance,
					docType,
					invoiceCopyType,
					selectedSite,
				});
			}
			if (docType === BULK_DOC_TYPES.MAKRO_INVOICE || docType === BULK_DOC_TYPES.CFW_INVOICE) {
				const pdfMakeInstance = pdfMake.createPdf(clone(docDefinition));
				separatedPDFs.push({
					pdfMakeInstance,
					docType,
					invoiceCopyType,
					selectedSite,
					summaryPhase,
				});
			}
		}
		return separatedPDFs;
		// // NOTE: following code will create 1 PDF for each site, where invoice and tech report are in the same PDF
		// let prevSite = null;
		// const footers = {};
		// let page = 0;
		// if(prevSite && prevSite !== selectedSite.table_name) {
		// 	const table_name = prevSite;
		// 	page = 0;
		// 	const footer = page => {
		// 		// if page is invoice page, add invoice footer
		// 		return footers[table_name][page-1] || false;
		// 	}
		// 	separatedDefinition = JSON.parse(JSON.stringify(separatedDefinition));
		// 	separatedDefinition.footer = footer;
		// 	const definition = separatedDefinition;
		// 	const separatedPDF = pdfMake.createPdf(definition);
		// 	separatedPDFs.push(separatedPDF);
		// 	separatedDefinition = { ...docDefinition, content: [], styles: {} };
		// }
		// if(separatedDefinition.content.length) {
		// 	separatedDefinition.content.push({ text: '', pageBreak: 'after' });
		// }
		// separatedDefinition.content.push(...docDefinition.content);
		// if(!footers[selectedSite.table_name]) { footers[selectedSite.table_name] = {} }
		// if(docType === BULK_DOC_TYPES.INVOICE) {
		// 	footers[selectedSite.table_name][page] = JSON.parse(JSON.stringify(docDefinition.footer));
		// }
		// separatedDefinition.styles = {...separatedDefinition.styles, ...docDefinition.styles}
		// if(index === docDefinitions.current.length - 1) {
		// 	const table_name = prevSite;
		// 	const footer = page => {
		// 		// if page is invoice page, add invoice footer
		// 		return footers[table_name][page-1] || false;
		// 	};
		// 	const definition = JSON.parse(JSON.stringify(separatedDefinition));
		// 	definition.footer = footer;
		// 	const separatedPDF = pdfMake.createPdf(definition);
		// 	separatedPDFs.push(separatedPDF);
		// }
		// if(docType === BULK_DOC_TYPES.INVOICE) {
		// 	page += nbInvoicePages;
		// } else if(docType === BULK_DOC_TYPES.TECH_REPORT) {
		// 	page += nbTechReportPages;
		// }
		// prevSite = selectedSite.table_name;
	};
	// create SEPARATED PDF by document type (copy invoice/billingDetail, copy Makro invoice, ---O&M report---)
	const createSeparatedPDFsByDocType = () => {
		let separatedDefinitions = {
			[BULK_DOC_TYPES.INVOICE]: null, // for copy invoices
			// [BULK_DOC_TYPES.TECH_REPORT]: null, // for O&M reports
		};
		for (let {
			docType,
			invoiceCopyType,
			selectedSite,
			docDefinition,
		} of docDefinitions.current) {
			if (
				(docType === BULK_DOC_TYPES.INVOICE ||
					docType === BULK_DOC_TYPES.BILLING_DETAIL ||
					docType === BULK_DOC_TYPES.MAKRO_INVOICE ||
					docType === BULK_DOC_TYPES.CFW_INVOICE) &&
				invoiceCopyType === INVOICE_COPY_TYPES.COPY
			) {
				if (!separatedDefinitions[BULK_DOC_TYPES.INVOICE]) {
					separatedDefinitions[BULK_DOC_TYPES.INVOICE] = {
						...docDefinition,
						content: [],
						styles: {},
					};
				}
				separatedDefinitions[BULK_DOC_TYPES.INVOICE].styles = {
					...separatedDefinitions[BULK_DOC_TYPES.INVOICE].styles,
					...docDefinition.styles,
				};
				if (separatedDefinitions[BULK_DOC_TYPES.INVOICE].content.length) {
					separatedDefinitions[BULK_DOC_TYPES.INVOICE].content.push({
						text: "",
						pageBreak: "after",
					});
				}
				separatedDefinitions[BULK_DOC_TYPES.INVOICE].content.push(
					...docDefinition.content,
				);
			}
		}
		separatedDefinitions = clone(separatedDefinitions);
		const separatedPDFs = {
			[BULK_DOC_TYPES.INVOICE]: pdfMake.createPdf(
				separatedDefinitions[BULK_DOC_TYPES.INVOICE],
			),
			// [BULK_DOC_TYPES.TECH_REPORT]: pdfMake.createPdf(separatedDefinitions[BULK_DOC_TYPES.TECH_REPORT]),
		};
		return separatedPDFs;
	};
	// create CLIENT_GROUPED PDF where PDFs are grouped by client
	const createClientGroupedPDFs = () => {
		const pdfsByClient = {}; // { $clientName: [ { docType, selectedSite, pdfMakeInstance } ] }
		const uniqueClients = [
			...new Set(
				docDefinitions.current.map((item) => item.selectedSite.value.clientID),
			),
		];
		for (let clientId of uniqueClients) {
			const docDefs = docDefinitions.current.filter(
				(item) => item.selectedSite.value.clientID === clientId,
			);
			for (let {
				docType,
				invoiceCopyType,
				selectedSite,
				docDefinition,
			} of docDefs) {
				const clientName = selectedSite.value.company;
				if (!pdfsByClient[clientName]) pdfsByClient[clientName] = [];
				const pdfMakeInstance = pdfMake.createPdf(clone(docDefinition));
				pdfsByClient[clientName].push({
					docType,
					selectedSite,
					pdfMakeInstance,
				});
			}
		}
		return pdfsByClient;
	};
	// The component generated PDFMake doc definition
	const onDocDefinitionForBulkActions = async ({
		docType,
		invoiceCopyType,
		selectedSite,
		docDefinition,
		summaryPhase,
	}) => {
		docDefinitions.current.push({
			docType,
			invoiceCopyType,
			selectedSite,
			docDefinition,
			summaryPhase,
		});
		docsToDownload.current.splice(0, 1); // remove the first arrray element
		// keep generating documents
		if (docsToDownload.current.length > 0) {
			const docToDownload = docsToDownload.current[0];
			setInvoicePreviewForBulkActions({});
			setMakroInvoicePreviewForBulkActions({});
			setCFWInvoicePreviewForBulkActions({});
			setTimeout(() => {
				// generate next document
				const allDocLength =
					docsToDownload.current.length + docDefinitions.current.length;
				const downloadedDocLength = docDefinitions.current.length;
				const text = savedToSend
					? `Generating documents for sending email...`
					: `Downloading ${downloadedDocLength} of ${allDocLength} documents`;
				setBulkDownloadProgress({
					text: text,
					allCount: allDocLength,
					loadedCount: downloadedDocLength,
					startTime: bulkDownloadProgress.startTime,
				});
				if (
					docToDownload.docType === BULK_DOC_TYPES.INVOICE ||
					docToDownload.docType === BULK_DOC_TYPES.BILLING_DETAIL ||
					docToDownload.docType === BULK_DOC_TYPES.TECH_REPORT
				) {
					setInvoicePreviewForBulkActions(docToDownload);
				} else if (docToDownload.docType === BULK_DOC_TYPES.MAKRO_INVOICE) {
					setMakroInvoicePreviewForBulkActions(docToDownload);
				} else if (docToDownload.docType === BULK_DOC_TYPES.CFW_INVOICE) {
					setCFWInvoicePreviewForBulkActions(docToDownload);
				}
			}, 10);
		}
		// if all documents are generated, download the combined PDF
		if (docsToDownload.current.length === 0) {
			// download combined PDF (1 PDF for all selected sites)
			if (
				docDefinitions.current.length &&
				clickedBulkAction.current === BULK_ACTIONS.COMBINED
			) {
				const combinedPDF = await createCombinedPDF();
				combinedPDF.download(
					`combined-${selectedMonthStr}.pdf`,
					resetBulkDownloadStates,
				);
				// download separated PDFs
			} else if (
				docDefinitions.current.length &&
				clickedBulkAction.current === BULK_ACTIONS.SEPARATED
			) {
				const zip = new JSZip();
				// and 1 PDF for all invoices of the selected sites
				const separatedPDFsBySite = createSeparatedPDFsBySite();
				// and 1 PDF for all O&M reports of the selected sites
				const separatedPDFsByDocType = createSeparatedPDFsByDocType();
				// loop over all PDFs to dowload
				const promises = [];
				for (let { pdfMakeInstance, docType, selectedSite, summaryPhase, invoiceCopyType } of separatedPDFsBySite) {
					const site_code = docType === BULK_DOC_TYPES.MAKRO_INVOICE ?
						`MAKRO${summaryPhase}` : // for makro invoice
						docType === BULK_DOC_TYPES.CFW_INVOICE ?
						`CFW${summaryPhase}` : // for CFW invoice
						invoiceSites.find(site => site.site_id === selectedSite.value.siteID)?.site_code; // for other sites invoice
					if (!site_code) { return alert('Site Code not found for site ID: "' + selectedSite.value.siteId + '"') }
					const copyType = invoiceCopyType ? invoiceCopyType.toLowerCase() : '';
					let fileName;
					if (docType === BULK_DOC_TYPES.INVOICE) {
						const invNo = selectedSite.value.invNo || 'unknown';
						fileName = `${invNo}_${copyType}.pdf`;
					} else if (docType === BULK_DOC_TYPES.MAKRO_INVOICE) {
						fileName = `${site_code}_invoice_summary_${copyType}_${selectedMonthStr}.pdf`;
					} else if (docType === BULK_DOC_TYPES.CFW_INVOICE) {
						fileName = `${site_code}_invoice_summary_${selectedMonthStr}.pdf`;
					} else if (docType === BULK_DOC_TYPES.BILLING_DETAIL) {
						const bdNo = selectedSite.value.bdNo || 'unknown';
						fileName = `${site_code}_${bdNo}_${copyType}.pdf`;
					} else if (docType === BULK_DOC_TYPES.TECH_REPORT) {
						fileName = `${site_code}_report_${selectedMonthStr}.pdf`;
					} else {
						fileName = `UNKNOWN_${selectedMonthStr}.pdf`;
					}
					const entityId = Array.isArray(selectedSite) ? selectedSite[0]?.entity_id :selectedSite.value.solarEntity?.entityID || 'unknown';
					const folderPath = `${selectedMonthStr}/${entityId}`;
					const downloadPromise = new Promise(resolve => {
						pdfMakeInstance.getBlob(blob => {
							zip.file(`${folderPath}/${fileName}`, blob);
							resolve();
						});
					});
					promises.push(downloadPromise);
				}
				const downloadInvoicePromise = new Promise((resolve) => {
					separatedPDFsByDocType[BULK_DOC_TYPES.INVOICE].getBlob((blob) => {
						zip.file(`invoices_${selectedMonthStr}.pdf`, blob);
						resolve();
					});
				});
				/* const downloadTechReportPromise = new Promise(resolve => {
					separatedPDFsByDocType[BULK_DOC_TYPES.TECH_REPORT].getBlob(blob => {
						zip.file(`performance_report_${selectedMonthStr}.pdf`, blob);
						resolve();
					});
				}); */
				promises.push(downloadInvoicePromise);
				/* promises.push(downloadTechReportPromise); */
				await Promise.all(promises);
				zip.generateAsync({ type: "blob" }).then((content) => {
					FileSaver.saveAs(content, `separate_${selectedMonthStr}.zip`);
				});
				resetBulkDownloadStates();
				// download PDF grouped by client
			} else if (
				docDefinitions.current.length &&
				clickedBulkAction.current === BULK_ACTIONS.CLIENT_GROUPED
			) {
				const zip = new JSZip();
				const clientGroupedPdfs = createClientGroupedPDFs();
				const clientNames = Object.keys(clientGroupedPdfs);
				const allPromises = [];
				for (let clientName of clientNames) {
					for (let doc of clientGroupedPdfs[clientName]) {
						const { docType, selectedSite, pdfMakeInstance } = doc;
						let fileDocType = "";
						if (
							docType === BULK_DOC_TYPES.INVOICE &&
							selectedSite.value.country === "VN"
						) {
							fileDocType = "Proforma Invoice";
						} else if (
							docType === BULK_DOC_TYPES.TECH_REPORT &&
							selectedSite.value.country === "VN"
						) {
							fileDocType = "Technical Report";
						}
						const fileName = `${fileDocType}_${selectedSite.value.projectID}_${selectedMonthStr}.pdf`;
						const promise = new Promise((resolve) => {
							pdfMakeInstance.getBlob((blob) => {
								zip.folder(clientName).file(fileName, blob);
								resolve();
							});
						});
						allPromises.push(promise);
					}
				}
				await Promise.all(allPromises);
				zip.generateAsync({ type: "blob" }).then((content) => {
					FileSaver.saveAs(content, `client_grouped_${selectedMonthStr}.zip`);
				});
				resetBulkDownloadStates();
			} else if (savedToSend) {
				setSavedToSend(false);
				try {
					const formData = new FormData();
					const separatedPDFsBySite = createSeparatedPDFsBySite();
					const promises = [];
					for (let {
						pdfMakeInstance,
						docType,
						selectedSite,
						summaryPhase,
						invoiceCopyType,
					} of separatedPDFsBySite) {
						const copyType = invoiceCopyType
							? invoiceCopyType.toLowerCase()
							: "";

						if (
							(copyType === "original" && docType === "INVOICE") ||
							docType === BULK_DOC_TYPES.TECH_REPORT
						) {
							const site_code = invoiceSites.find(
								(site) => site.site_id === selectedSite.value.siteID,
							)?.site_code; // for other sites invoice
							if (!site_code) {
								return alert(
									'Site Code not found for site ID: "' +
										selectedSite.value.siteId +
										'"',
								);
							}
							const docTypeString =
								docType === BULK_DOC_TYPES.TECH_REPORT
									? "technical_report"
									: `invoice_${copyType}`;
							const downloadPromise = new Promise((resolve) => {
								pdfMakeInstance.getBlob((blob) => {
									formData.append(
										"files[]",
										blob,
										`${site_code}_${docTypeString}_${selectedMonthStr}.pdf`,
									);
									resolve();
								});
							});
							promises.push(downloadPromise);
						}
					}
					const details = separatedPDFsBySite[0].selectedSite.value;
					const site_id = details.siteID;
					const customer_name = details.solo
						? details.company
						: `${details.company} ${details.siteName}`;
					const monthly_id = invoiceSites.find(
						(site) => site.site_id === details.siteID,
					)?.id; // for other sites invoice
					formData.append("selectedMonthStr", selectedMonthStr);
					formData.append("customer_name", customer_name);
					formData.append("FTcost", details.FTcost);
					formData.append("email_sender", `${user.email}`);
					formData.append("invoice_quarter", details.monthRange.engQuarter);
					try {
						await Promise.all(promises).then(() => {
							GYAxios.post(
								`/invoice/send_email/site_id/${site_id}/recipient/${emailsRecipients}/monthly/${monthly_id}/saved/${reSaveRecipients}`,
								formData,
								{
									headers: {
										"Content-Type": "multipart/form-data",
									},
								},
							)
								.then((res) => {
									setReSaveRecipients(false);
									if (res.status == 200) {
										Noti.create({ type: "s", msg: res.data.message });
									} else {
										Noti.create({ type: "e", msg: res.data.message });
									}
									setTimeout(() => {
										resetBulkDownloadStates();

										GYAxios.get(
											"/invoice/email_logs?month=" +
												selectedMonth.format("YYYY-MM"),
										).then((res) => {
											if (res.status == 200) {
												setExistingEmailLog(res.data);
											} else {
												setExistingEmailLog(null);
												Noti.create({
													type: "e",
													msg: "Failed to get Email log - Button for emailing cannot be used!",
												});
											}
										});
										props.setReady(false);
									}, 1000);
								})
								.catch((err) => {
									setReSaveRecipients(false);
									let msg = "";
									if (err.response.data.message) {
										msg = err.response.data.message;
									} else if (err.response.statusText) {
										msg = err.response.statusText;
									} else {
										msg = "Please contact IT for this error.";
									}
									Noti.create({ type: "e", msg: msg });
									resetBulkDownloadStates();
								});
						});
					} catch (error) {
						let msg = "";
						if (error.response.data.message) {
							msg = error.response.data.message;
						} else if (error.response.statusText) {
							msg = error.response.statusText;
						} else {
							msg = "Please contact IT for this error.";
						}
						Noti.create({ type: "e", msg: msg });
						resetBulkDownloadStates();
					}
				} catch (error) {
					const msg = "Please contact IT for this error.";
					Noti.create({ type: "e", msg: msg });
					resetBulkDownloadStates();
				}
			} else {
				resetBulkDownloadStates();
			}
		}
	};

	// handle bulk download on click
	const onBulkDownloadClick = (bulkAction) => {
		setIsBulkDownloading({ value: "true", bulkAction });
		clickedBulkAction.current = bulkAction;
		const selectedSites = getSelectedSites();
		if (selectedSites.length) {
			docsToDownload.current = [];
			for (let site of selectedSites) {
				const selectedSite = formatSelectedSite(site);
				if (site.country === "TH" && site.client_id !== makroClientId) {
					// none makro sites
					docsToDownload.current.push({
						selectedSite,
						isTechReport: false,
						invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
						docType: BULK_DOC_TYPES.INVOICE,
					});
					docsToDownload.current.push({
						selectedSite,
						isTechReport: false,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						docType: BULK_DOC_TYPES.INVOICE,
					});
				}
				if (site.country === "TH" && site.client_id === makroClientId) {
					// makro sites
					docsToDownload.current.push({
						selectedSite,
						isTechReport: false,
						invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
						docType: BULK_DOC_TYPES.BILLING_DETAIL,
					});
					docsToDownload.current.push({
						selectedSite,
						isTechReport: false,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						docType: BULK_DOC_TYPES.BILLING_DETAIL,
					});
				}
				if (site.country === "VN") {
					docsToDownload.current.push({
						selectedSite,
						isTechReport: false,
						invoiceCopyType: null,
						docType: BULK_DOC_TYPES.INVOICE,
					});
				}
				docsToDownload.current.push({
					selectedSite,
					isTechReport: true,
					docType: BULK_DOC_TYPES.TECH_REPORT,
				});
			}
			// if all Makro sites selected, also produce makro invoice
			if (
				country === "TH" &&
				(isGYTHUser(roles) ||
					(isClientTHUser(roles) &&
						isClientHQUser(userInfo) &&
						isMakroClientUser(userInfo)))
			) {
				if (
					invoiceSites
						.filter(
							(site) =>
								site.client_id === makroClientId &&
								!makroTestSites.includes(site.table_name),
						)
						.every((site) =>
							selectedSites.find((s) => s.site_id === site.site_id),
						)
				) {
					let makroInvoiceSitesP1 = props.summaryInvoiceSites.filter(
						props.makroSitesFilterByPhaseFn("PHASE_1"),
					);
					let makroInvoiceSitesP2 = props.summaryInvoiceSites.filter(
						props.makroSitesFilterByPhaseFn("PHASE_2"),
					);
					if (!makroInvoiceSitesP1.length) {
						makroInvoiceSitesP1 = null;
					}
					if (!makroInvoiceSitesP2.length) {
						makroInvoiceSitesP2 = null;
					}
					// Makro Phase 1
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.MAKRO_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
						makroSites: makroInvoiceSitesP1,
						bankAccount: makroInvoiceSitesP1?.[0]?.bankAccount || null,
						selectedPhase: "",
					});
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.MAKRO_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						makroSites: makroInvoiceSitesP1,
						bankAccount: makroInvoiceSitesP1?.[0]?.bankAccount || null,
						selectedPhase: "",
					});
					// Makro Phase 2
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.MAKRO_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
						makroSites: makroInvoiceSitesP2,
						bankAccount: makroInvoiceSitesP2?.[0]?.bankAccount || null,
						selectedPhase: "2",
					});
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.MAKRO_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						makroSites: makroInvoiceSitesP2,
						bankAccount: makroInvoiceSitesP2?.[0]?.bankAccount || null,
						selectedPhase: "2",
					});
				}
			}

			// if any CFW sites selected, also produce CFW summary invoice
			if (
				country === "TH" &&
				(isGYTHUser(roles) ||
					(isClientTHUser(roles) &&
						isClientHQUser(userInfo) &&
						isCFWClientUser(userInfo)))
			) {
				if (
					invoiceSites
						.filter(
							(site) =>
								site.client_id === CFWClientId,
						)
						.every((site) =>
							selectedSites.find((s) => s.site_id === site.site_id),
						)
				) {
					let cfwInvoiceSitesGYS6 = props.summaryInvoiceSites.filter(
						props.CFWSitesFilterByPhaseFn("PHASE_GYS6"),
					);
					let cfwInvoiceSitesS4 = props.summaryInvoiceSites.filter(
						props.CFWSitesFilterByPhaseFn("PHASE_S4"),
					);
					if (!cfwInvoiceSitesGYS6.length) {
						cfwInvoiceSitesGYS6 = null;
					}
					if (!cfwInvoiceSitesS4.length) {
						cfwInvoiceSitesS4 = null;
					}
					// CFW Phase GYS6
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.CFW_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						cfwSites: cfwInvoiceSitesGYS6,
						bankAccount: cfwInvoiceSitesGYS6?.[0]?.bankAccount || null,
						selectedPhase: "GYS6",
					});
					// CFW Phase S4
					docsToDownload.current.push({
						docType: BULK_DOC_TYPES.CFW_INVOICE,
						invoiceCopyType: INVOICE_COPY_TYPES.COPY,
						cfwSites: cfwInvoiceSitesS4,
						bankAccount: cfwInvoiceSitesS4?.[0]?.bankAccount || null,
						selectedPhase: "S4",
					});
				}
			}

			setInvoicePreviewForBulkActions(docsToDownload.current[0]);
			setBulkDownloadProgress({
				text: `Downloading 1 of ${docsToDownload.current.length} documents`,
				allCount: docsToDownload.current.length,
				loadedCount: 0,
				startTime: new Date().getTime(),
			});
		} else {
			resetBulkDownloadStates();

			// show warning message when there is no invoice available to download
			if (selectedRows.length) {
				message.warning(
					"Invoice(s) you selected have not yet been approved. You can only download approved invoices.",
				);
			} else {
				message.warning("Please select invoice(s) before downloading them.");
			}
		}
	};

	useEffect(() => {
		window.onbeforeunload = onBeforeUnload;
	}, [onBeforeUnload]);

	useEffect(() => {
		if (!bulkDownloadProgress) {
			return setBulkDownloadProgressWarning("");
		}
		if (bulkDownloadProgress) {
			const { loadedCount, startTime } = bulkDownloadProgress;
			if (
				!bulkDownloadProgressWarning &&
				loadedCount &&
				(new Date().getTime() - startTime) / 1000 > loadedCount * 2 * 1.3
			) {
				// it normally takes 2s to download 1 document, if it takes 30% more time, show warning
				setBulkDownloadProgressWarning(
					"It took longer than expected to download the documents. Please check your internet connection.",
				);
			}
			if (
				bulkDownloadProgressWarning &&
				loadedCount &&
				(new Date().getTime() - startTime) / 1000 <= loadedCount * 2 * 1.3
			) {
				// if it takes less time, remove warning
				setBulkDownloadProgressWarning("");
			}
		}
	}, [bulkDownloadProgress, bulkDownloadProgressWarning]);

	// ----------------------- Finish Bulk actions -----------------------

	// View invoices/reports of current month role
	const viewCurrMonth = roles.includes(
		"administration:InvoiceTable:_viewInvCurMonth",
	);
	// Clients' access to invoice role
	const clientViewInv = roles.includes(
		"administration:InvoiceTable:_clientViewInv",
	);

	// Min date of datepicker
	// Client TH		: 2021-04-01
	// Client VN		: 2021-06-01
	// GY TH			 	: 2020-11-01
	// GY VN			 	: 2021-06-01
	let minDate = "";

	switch (country) {
		case "TH":
			if (roles != null && clientViewInv)
				minDate = moment("2021-04-01", "YYYY-MM-DD").toDate();
			else minDate = moment("2020-11-01", "YYYY-MM-DD").toDate();
			break;

		case "VN":
			minDate = moment("2021-06-01", "YYYY-MM-DD").toDate();
			break;

		case "PH":
			minDate = moment("2025-06-01", "YYYY-MM-DD").toDate();
			break;

		default:
			break;
	}

	// Max date of datepicker
	// Regular		: Previous month
	// With Role	: Current month
	const maxDate =
		roles != null && viewCurrMonth
			? moment().add(1, "month").startOf("month").toDate()
			: moment().startOf("month").toDate();

	// Approval permissions
	const approvalPermissions = props.approvalPermissions;

	// Months
	const months = [
		{ engMonth: "January", thMonth: "มกราคม", phMonth: "Enero" },
		{ engMonth: "February", thMonth: "กุมภาพันธ์", phMonth: "Pebrero" },
		{ engMonth: "March ", thMonth: "มีนาคม", phMonth: "Marso" },
		{ engMonth: "April ", thMonth: "เมษายน", phMonth: "Abril" },
		{ engMonth: "May", thMonth: "พฤษภาคม", phMonth: "Mayo" },
		{ engMonth: "June", thMonth: "มิถุนายน", phMonth: "Hunyo" },
		{ engMonth: "July", thMonth: "กรกฎาคม", phMonth: "Hulyo" },
		{ engMonth: "August", thMonth: "สิงหาคม", phMonth: "Agosto" },
		{ engMonth: "September", thMonth: "กันยายน", phMonth: "Setyembre" },
		{ engMonth: "October", thMonth: "ตุลาคม", phMonth: "Oktubre" },
		{ engMonth: "November", thMonth: "พฤศจิกายน", phMonth: "Nobyembre" },
		{ engMonth: "December", thMonth: "ธันวาคม", phMonth: "Disyembre" },
	];

	// Month quarters
	const quartersMonth = [
		{
			engQuarter: `January ${engYear} - April ${engYear}`,
			thQuarter: `มกราคม ${thYear} - เมษายน ${thYear}`,
		},
		{
			engQuarter: `May ${engYear} - August ${engYear}`,
			thQuarter: `พฤษภาคม ${thYear} - สิงหาคม ${thYear}`,
		},
		{
			engQuarter: `September ${engYear} - December ${engYear}`,
			thQuarter: `กันยายน ${thYear} - ธันวาคม ${thYear}`,
		},
	];

	// Country select options
	const countries = [
		{ label: "Thailand", value: "TH" },
		{ label: "Vietnam", value: "VN" },
		{ label: "Cambodia", value: "KH" },
		{ label: "Philippines", value: "PH" },
	];

	// Country select style
	const selectStyle = {
		container: (provided, state) => ({
			...provided,
			marginRight: 0,
			width: "35%",
			float: "right",
			marginLeft: "1em",
			zIndex: 100,
		}),
		option: (provided, state) => ({
			...provided,
			fontSize: "13px",
		}),
		menuList: (provided, state) => ({
			...provided,
			maxHeight: "500px",
			color: "black",
			textAlign: "left",
		}),
		groupHeading: (provided, state) => ({
			...provided,
			fontSize: "13px",
			color: "#a4aaaa",
			borderTop: "#a4aaaa 1px solid",
		}),
		singleValue: (provided, state) => {
			const fontSize = "13px";
			return { ...provided, fontSize };
		},
	};

	/**
	 * Get phase of Makro sites
	 * @param {Site code of Makro sites} siteCode
	 */
	const getPhase = (siteCode) => {
		let phase = "";
		if (siteCode.includes("MKO")) {
			if (siteCode.length === 5) phase = "(P1)";
			else if (siteCode.length === 6) phase = "(P2)";
		}
		return phase;
	};

	// NOTE: The code of this function is copied and a bit modified from useEffect in InvoicePreview.js
	// where it reshapes selectedSite in useEffect (dependencies: props.selecetedSite, props.isTechReport)
	const getSiteData = async (selectedSite) => {
		const date = moment(selectedSite.value.date, "DD/MM/YYYY");
		const selectedMonth = date.format("YYYYMM");
		const tempSelected = selectedSite;

		const site = tempSelected.value;
		const tableName = tempSelected.table_name;
		const contractID = site.contractID != null ? site.contractID : "";

		// Site is FIT site
		const isFITSite = contractID.includes("FIT");

		// Request to get FIT price
		/* const reqFITPrice = GYAxios.get(`/reports/price/FIT/${selectedMonth}`); */
		// prepare an array to add to GYAxios
		const reqFITPrice = "/reports/price/FIT/" + selectedMonth;
		const reqEVNInput = `/evn_input/site/${tableName}/month/${selectedMonth}`;
		const reqGetBillingChanged = `/invoice/billing_info/${tableName}/${selectedMonth}`;
		const reqGetOldTariff = `/invoice/old_tariff/${selectedMonth}?tariff_id=${site.tariffData.tariffID}`;

		let FITPrices = { FITOn: 0, FITOff: 0 };
		const FITPriceData = await GYAxios.get(reqFITPrice);
		if (!FITPriceData.error) FITPrices = FITPriceData.data[0];

		let EVNInputs = null;
		let moreEVNInputs = null;
		if (isFITSite) {
			const EVNInputsData = await GYAxios.get(reqEVNInput);
			if (EVNInputsData != null && !EVNInputsData.error) {
				if (
					Array.isArray(EVNInputsData.data) &&
					EVNInputsData.data.length > 0
				) {
					EVNInputs = EVNInputsData.data[0];
					if (EVNInputsData.data.length > 1) {
						moreEVNInputs = EVNInputsData.data; // an array to contain many periods of EVN records for a specific month
					}
				}
			}
		}

		let isBillingChanged = false;
		if (site.additional_info != null) {
			if (site.additional_info.invoice != null) {
				if (site.additional_info.invoice.billing_changed != null) {
					isBillingChanged = site.additional_info.invoice.billing_changed;
				}
			}
		}

		let billingChangedData = null;
		if (isBillingChanged) {
			const billingChangedDataRes = await GYAxios.get(reqGetBillingChanged);
			if (billingChangedDataRes != null && !billingChangedDataRes.error) {
				if (
					Array.isArray(billingChangedDataRes.data) &&
					billingChangedDataRes.data.length > 0
				) {
					billingChangedData = billingChangedDataRes.data[0];
				}
			}
		}

		let oldTariffData = null;
		if (site.old_price) {
			const oldTariffDataRes = await GYAxios.get(reqGetOldTariff);
			if (oldTariffDataRes != null && !oldTariffDataRes.error) {
				if (
					Array.isArray(oldTariffDataRes.data) &&
					oldTariffDataRes.data.length > 0
				) {
					oldTariffData = oldTariffDataRes.data[0];
				}
			}
		}

		const siteValues = {
			...tempSelected.value,
			...FITPrices,
			EVNInputs,
			moreEVNInputs,
			billingName:
				billingChangedData != null
					? billingChangedData.billing_name
					: site.billingName,
			billingAddress:
				billingChangedData != null
					? billingChangedData.billing_address
					: site.billingAddress,
			old_price: oldTariffData != null ? oldTariffData : null,
		};

		const finalRes = {
			...tempSelected,
			value: siteValues,
		};
		return finalRes;
	};

	// NOTE: The code of this function is partially copied from generateInvoice() in InvoiceTemplate_VN.js
	// this block of code is also modified to comply with the requirements of Payment Records
	const calculateSubtotalVatTotal = async (siteData) => {
		let _siteData = siteData;
		const isHavingModifiedTariff = _siteData.value.old_price != null;
		const clientAmount = { subtotal: 0, vat_amount: 0, total: 0 };
		let govAmount = null;

		const data = siteData.value;
		const contractID = data.contractID;
		const EVNInputs = data.EVNInputs
			? { ...data.EVNInputs, FITOn: data.FITOn, FITOff: data.FITOff }
			: null;
		// NOTE: comment out below lines because payment record requires different flow of calculation
		// revenue sharing
		// let SRParams = null;
		// if(EVNInputs) {
		// 	SRParams = solarCalc.calculateSharedRevenue(
		// 		data.contractID,
		// 		data.discountFT,
		// 		EVNInputs.EVN_total,
		// 		EVNInputs.FITOn
		// 	);
		// }
		// const SRSubtotal = SRParams ? SRParams.subtotal : 0;

		const getPriceObj = (data) =>
			data.contractID === "FIT"
				? { priceON: data.FITOn, priceOFF: data.FITOff }
				: { priceON: data.priceON, priceOFF: data.priceOFF };
		const priceObj = getPriceObj(data);
		const price = {
			ON: priceObj.priceON,
			OFF: priceObj.priceOFF,
			FT: data.FTcost,
		};

		const discount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			FT: data.discountFT,
		};

		const currency = data.currency;

		if (isHavingModifiedTariff) {
			const {
				costObj: { costsObj_prev, costsObj_curr },
			} = await calculateForChangingTariff({
				table_name: _siteData.table_name,
				contractID,
				EVNInputs,
				old_price: data.old_price,
				dateStr: data.dateStr,
				FTcost: data.FTcost,
				prices_curr: price,
				discount,
				currency,
				moreEVNInputs: data.moreEVNInputs,
			});

			if (contractID.includes("FIT")) {
				// FIT, SCFITCL, SCFITGY
				clientAmount.subtotal =
					costsObj_prev.costON +
						costsObj_prev.costOFF +
						costsObj_curr.costON +
						costsObj_curr.costOFF +
						data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}

			if (contractID === "SC") {
				clientAmount.subtotal =
					costsObj_prev.costTotal +
						costsObj_curr.costTotal +
						data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
		} else {
			const prodData = {
				solarON: data.solarON,
				solarOFF: data.solarOFF,
				solarTotal: data.solarTotal,
				contractID: data.contractID,
			};
			const prodObj = solarCalc.calculateProd(prodData, EVNInputs);
			const prod = {
				ON: prodObj.solarON,
				OFF: prodObj.solarOFF,
				TOTAL: prodObj.solarTotal,
			};

			// calculate subtotal, VAT, total based on contract ID
			if (contractID.includes("FIT")) {
				// FIT, SCFITCL, SCFITGY
				const costsObj = solarCalc.calculateProdCost(
					prod,
					price,
					discount,
					currency,
				);
				clientAmount.subtotal =
					costsObj.costON + costsObj.costOFF + data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
			if (contractID === "SC") {
				const costsObj = solarCalc.calculateProdCost(
					prod,
					price,
					discount,
					currency,
				);
				clientAmount.subtotal = costsObj.costTotal + data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
		}

		// for government payment record only (in payment record, it will have proforma invoice number suffixed with "02")
		if (contractID === "SCFITGY") {
			govAmount = {};
			if (data.moreEVNInputs) {
				const _EVNs = data.moreEVNInputs;
				const EVN_total = _EVNs.reduce((acc, curr) => acc + curr.EVN_total, 0);
				const SRParams = solarCalc.calculateSharedRevenue(
					contractID,
					data.discountFT, // only for SCFITGY, this is in fact shared revenue percent, not FT discount
					EVN_total,
					EVNInputs.FITOn,
				);
				govAmount.sharingRevenueAmount = SRParams.subtotal; // EVN_total * EVN_prod_total * shared_revenue_percent
				govAmount.subtotal =
					EVN_total * EVNInputs.FITOn + data.specialDiscount ?? 0; // EVN_total * EVN_prod_total
				govAmount.vat_amount = govAmount.subtotal * data.vat;
				govAmount.total = govAmount.subtotal + govAmount.vat_amount;
			} else {
				const SRParams = solarCalc.calculateSharedRevenue(
					contractID,
					data.discountFT, // only for SCFITGY, this is in fact shared revenue percent, not FT discount
					EVNInputs.EVN_total,
					EVNInputs.FITOn,
				);
				govAmount.sharingRevenueAmount = SRParams.subtotal; // EVN_total * EVN_prod_total * shared_revenue_percent
				govAmount.subtotal =
					EVNInputs.EVN_total * EVNInputs.FITOn + data.specialDiscount ?? 0; // EVN_total * EVN_prod_total
				govAmount.vat_amount = govAmount.subtotal * data.vat;
				govAmount.total = govAmount.subtotal + govAmount.vat_amount;
			}
		}

		return { clientAmount, govAmount };

		// subtotal
		// NOTE: comment out below lines because payment record
		// let subtotalPrice = isSCFIT
		// 	? solarCalc.parseFormatNumber(costsObj.costTotal + SRSubtotal)
		// 	: costsObj.costTotal;
		// let subtotalPrice = costsObj.costTotal;

		// // VAT amount
		// const vatPercent = data.vat;
		// const VATObj = solarCalc.calculateVAT(
		// 	subtotalPrice,
		// 	data.currency,
		// 	vatPercent
		// );
		// const VATPrice = VATObj.VAT;

		// // total
		// const tempTotal = VATObj.totalDue;
		// // NOTE: comment out below lines because VN doesn't have special discount
		// // let discountPrice = 0;
		// // let totalDue = 0;
		// // if (data.specialDiscount === null) {
		// // 	totalDue = tempTotal;
		// // } else {
		// // 	// Special discounts
		// // 	discountPrice = -(tempTotal * data.specialDiscount);
		// // 	totalDue =
		// // 		parseFloat(subtotalPrice.toFixed(2)) +
		// // 		parseFloat(VATPrice.toFixed(2)) +
		// // 		parseFloat(discountPrice.toFixed(2));
		// // }
		// const totalDue = tempTotal;

		// const subtotal = subtotalPrice;
		// const vat_amount = VATPrice;
		// const total = totalDue;
		// return { subtotal, vat_amount, total };
	};

	const handleRecordPaymentVN = async (vatInvoiceVN) => {
		const proforma_inv_no = selectedSite.value.invNo;
		const credit_term = selectedSite.value.creditTerm;
		const updated_by = user.email;
		const { vat_inv_no, vat_inv_date } = vatInvoiceVN;
		const siteData = await getSiteData(selectedSite);
		const {
			clientAmount: { subtotal, vat_amount, total },
			govAmount,
		} = await calculateSubtotalVatTotal(siteData);
		const contract_id = previewInvoice.contract_id;

		await GYAxios.post("/payment_record_vn/", {
			proforma_inv_no,
			credit_term,
			updated_by,
			vat_inv_date,
			vat_inv_no,
			subtotal,
			vat_amount,
			total,
			contract_id,
			govAmount,
		});
	};

	const calculateForChangingTariff = async (siteObject) => {
		const table_name = siteObject.table_name;
		const contractID = siteObject.contractID;
		const EVNInputs = siteObject.EVNInputs;
		const FTcost = siteObject.FTcost;
		const discount = siteObject.discount;
		const currency = siteObject.currency;
		const prices_curr = siteObject.prices_curr;

		let moreEVNInputs =
			siteObject.moreEVNInputs != null ? siteObject.moreEVNInputs : null;

		const old_price_obj = siteObject.old_price;
		const old_price_start_date = moment(siteObject.dateStr)
			.startOf("month")
			.format("YYYY-MM-DD");
		const old_price_end_date = moment(old_price_obj.end_date).format(
			"YYYY-MM-DD",
		);
		const current_price_start_date = moment(old_price_end_date)
			.add(1, "days")
			.format("YYYY-MM-DD");
		const current_price_end_date = moment(siteObject.dateStr)
			.endOf("month")
			.format("YYYY-MM-DD");
		const url_production = [
			`/invoice/daily_prod?fromDate=${old_price_start_date}&toDate=${old_price_end_date}&table_name=${table_name}`,
			`/invoice/daily_prod?fromDate=${current_price_start_date}&toDate=${current_price_end_date}&table_name=${table_name}`,
		];

		const prodData = await Axios.all([
			GYAxios.get(url_production[0]),
			GYAxios.get(url_production[1]),
		])
			.then(
				Axios.spread((...responses) => {
					const _prodData = responses.map((res) => res.data);
					return _prodData;
				}),
			)
			.catch((errors) => {
				console.log(errors);
			});

		let evn_01 = null;
		let evn_02 = null;

		if (moreEVNInputs != null) {
			evn_01 = moreEVNInputs.filter(
				(evn) =>
					moment(evn.start_date).format("YYYY-MM-DD") == old_price_start_date,
			);
			evn_02 = moreEVNInputs.filter(
				(evn) =>
					moment(evn.start_date).format("YYYY-MM-DD") ==
					current_price_start_date,
			);

			if (evn_01.length === 0) evn_01 = null;
			else evn_01 = evn_01[0];
			if (evn_02.length === 0) evn_02 = null;
			else evn_02 = evn_02[0];
		} else {
			evn_01 = null;
			evn_02 = EVNInputs;
		}

		const prodData_prev = prodData[0][0];
		const prodData_curr = prodData[1][0];

		const _prodData_prev = {
			solarON: prodData_prev.solar_ON,
			solarOFF: prodData_prev.solar_OFF,
			solarTotal: prodData_prev.solar_total,
			contractID: contractID,
		};

		const _prodData_curr = {
			solarON: prodData_curr.solar_ON,
			solarOFF: prodData_curr.solar_OFF,
			solarTotal: prodData_curr.solar_total,
			contractID: contractID,
		};

		const _evnDataDaily = { _evn_prev: evn_01, _evn_curr: evn_02 };

		if (EVNInputs != null) {
			_evnDataDaily._evn_prev = {
				EVN_on: evn_01?.EVN_on || 0,
				EVN_off: evn_01?.EVN_off || 0,
				EVN_total: evn_01?.EVN_total || 0,
			};
			_evnDataDaily._evn_curr = {
				EVN_on: evn_02?.EVN_on || 0,
				EVN_off: evn_02?.EVN_off || 0,
				EVN_total: evn_02?.EVN_total || 0,
			};
		}

		/* if (EVNInputs?.needTakenDaily === 1) {
			_evnDataDaily._evn_prev = {
				EVN_on: prodData_prev.EVN_on,
				EVN_off: prodData_prev.EVN_off,
				EVN_total: prodData_prev.EVN_total,
			}
			_evnDataDaily._evn_curr = {
				EVN_on: prodData_curr.EVN_on,
				EVN_off: prodData_curr.EVN_off,
				EVN_total: prodData_curr.EVN_total,
			}
		} */
		const calculated_prod_EVN_prev = solarCalc.calculateProd(
			_prodData_prev,
			_evnDataDaily._evn_prev,
		);
		const calculated_prod_EVN_curr = solarCalc.calculateProd(
			_prodData_curr,
			_evnDataDaily._evn_curr,
		);

		const prodData_prev_ = {
			ON: calculated_prod_EVN_prev.solarON,
			OFF: calculated_prod_EVN_prev.solarOFF,
			TOTAL: calculated_prod_EVN_prev.solarTotal,
		};

		const prodData_curr_ = {
			ON: calculated_prod_EVN_curr.solarON,
			OFF: calculated_prod_EVN_curr.solarOFF,
			TOTAL: calculated_prod_EVN_curr.solarTotal,
		};

		const prices_prev = {
			ON: old_price_obj.price_on,
			OFF: old_price_obj.price_off,
			FT: FTcost,
		};

		const costsObj_prev = solarCalc.calculateProdCost(
			prodData_prev_,
			prices_prev,
			discount,
			currency,
		);
		const costsObj_curr = solarCalc.calculateProdCost(
			prodData_curr_,
			prices_curr,
			discount,
			currency,
		);
		const costObj = { costsObj_prev, costsObj_curr };

		return { costObj };
	};

	const reload = useCallback(async () => {
		props.setReady(false);
		if (country == "TH") {
			GYAxios.get(
				"/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
			).then((res) => {
				if (res.status == 200) {
					setExistingEmailLog(res.data);
				} else {
					setExistingEmailLog(null);
					Noti.create({
						type: "e",
						msg: "Failed to get Email log - Button for emailing cannot be used!",
					});
				}
			});
		}
	}, [country, selectedMonth, props]);

	async function updateInvoiceDate(monthly_id) {
		const ques = "Enter the new date for the invoice (format: YYYY-MM-DD)";
		const newDate = prompt(ques);

		const dateFormat = /^\d{4}-\d{2}-\d{2}$/; // Matches 'YYYY-MM-DD'
		if (dateFormat.test(newDate) && monthly_id != null) {
			const isValid = dayjs(newDate, "YYYY-MM-DD", true).isValid();
			if (isValid) {
				await GYAxios.patch(
					`/invoice/update_invoice_date?param[monthly_id]=${monthly_id}&param[invoice_date]=${newDate}`,
				);
				alert("Invoice date updated!");
				reload();
			} else {
				// return error message:
				alert("Invalid date format");
			}
		} else {
			// return error message:
			alert("Invalid date format");
		}
	}

	/**
	 * Toggle grid energy reinjection status for a site
	 * @param {Object} site - The site object containing invoice data
	 */
	const handleGridEnergyReinjection = useCallback(
		async (site) => {
			const currentStatus = site.is_grid_reinject_applied === 1;
			const actionText = currentStatus
				? "remove grid energy reinjection data from"
				: "apply grid energy reinjection data to";
			const confirmMessage = `Are you sure you want to ${actionText} this invoice?`;

			if (window.confirm(confirmMessage)) {
				try {
					const response = await GYAxios.post(
						"/invoice/grid_energy_reinjection",
						{
							dateData: moment(site.dateData, "YYYY-MM-DDTHH:mm:ssZ").format(
								"YYYY-MM-DD",
							),
							table_name: site.table_name,
							is_grid_reinject_applied: currentStatus ? 0 : 1,
						},
					);

					if (response.data.success) {
						Noti.create({
							type: "success",
							msg: response.data.message,
						});
						reload(); // Reload the table to reflect changes
					} else {
						Noti.create({
							type: "error",
							msg: "Failed to update grid energy reinjection status",
						});
					}
				} catch (error) {
					console.error("Error updating grid energy reinjection:", error);
					Noti.create({
						type: "error",
						msg:
							error.response?.data?.error ||
							"An error occurred while updating grid energy reinjection status",
					});
				}
			}
		},
		[reload],
	);

	/**
	 * Update status of invoice
	 *
	 * NOTE: it is here and not in ApprovalContents because of
	 * Noti component. notifications will disappear once
	 * the component is destroyed
	 * @param {Approval process - APPROVE, REJECT, RESOLVE} process
	 * @param {Reject invoice remark} remark
	 */
	const updateInvoiceStatus = (process, remark, vatInvoiceVN = {}) => {
		let param = null;
		let approvalStatus = "";
		let inv_issued_date = "";
		let resMsg = "";
		const invoiceData = previewInvoice;

		const freeSites = props.freeSites;

		const isFreeMakroSite = freeSites.includes(invoiceData.table_name);

		const contractID = previewInvoice.contract_id;
		let isFITorSCFITSite = contractID.includes("FIT");
		const approvedStatus = 3;

		switch (process) {
			case "APPROVE":
				approvalStatus = parseInt(previewInvoice.approvalStatus) + 1;
				inv_issued_date =
					previewInvoice.inv_issued_date == null
						? moment(selectedMonth, "YYYY-MM-DD")
								.endOf("month")
								.format("YYYY-MM-DD")
						: moment(previewInvoice.inv_issued_date).format("YYYY-MM-DD");
				if (approvalStatus === 2 && isOMUser(roles) && isGYTHUser(roles)) {
					approvalStatus = parseInt(previewInvoice.approvalStatus) + 2;
				}
				param = {
					approvalStatus,
					inv_issued_date,
				};

				const isApprovedByAcct = approvalStatus === approvedStatus;

				// Lock monthly_calc if approved by accounting
				if (isApprovedByAcct) {
					const invoiceNo = !isMakroSite(previewInvoice.client_id)
						? buildInvoiceReceiptNumber({
								type: "INV",
								solarEntityId: previewInvoice.entity_id,
								date: previewInvoice.dateData,
								siteCode: previewInvoice.site_code,
								isMSA: previewInvoice.operation_status === 2,
							})
						: getMakroInvoiceNo(
								previewInvoice.site_code,
								previewInvoice.dateData,
							);

					param = { ...param, locked: 1 };

					if (!isFreeMakroSite) param = { ...param, invoice_no: invoiceNo };
					previewInvoice.invoiceNo = invoiceNo;
				}

				resMsg = "Invoice approved!";
				break;
			case "REJECT":
				if (remark != null) {
					const _approvalStatus = isAcctUser ? 1 : -1;
					approvalStatus = parseInt(_approvalStatus);
					param = { rejectedRemark: remark, approvalStatus };
				}
				resMsg = "Invoice rejected!";
				break;
			case "RESOLVE":
				approvalStatus = Math.abs(parseInt(previewInvoice.approvalStatus));
				param = { approvalStatus, rejectedRemark: null };
				resMsg = "Invoice resolved!";
				break;

			default:
				break;
		}

		if (param != null) {
			const finalParam = { id: previewInvoice.id, param };
			const isApprovedByAcct = approvalStatus === approvedStatus;

			try {
				if (isFITorSCFITSite && isApprovedByAcct)
					handleEVNInputs(previewInvoice);

				GYAxios.patch("/reports/update_monthly", finalParam).then((res) => {
					if (res.data.error != null) {
						// Show failed notification
						Noti.create({
							type: "e",
							msg: "Data update failed..",
						});
					} else {
						// Show success notification
						Noti.create({
							type: "s",
							msg: resMsg,
						});

						// Refetch updated data
						props.setReady(false);

						// Create a new receipt

						// saving approval status to a log in reports DB
						GYAxios.post(
							`/reports/approval_log?monthly_id=${finalParam.id}&monthly_table=${invoiceData.table_name}&approved_by=${userInfo.email}&approval_status=${finalParam.param.approvalStatus}`,
						);

						// Only for TH sites
						if (
							previewInvoice.country === "TH" &&
							!isFreeMakroSite &&
							approvalStatus === approvedStatus
						)
							handleReceipt(previewInvoice);

						// Only for VN sites having
						if (
							showVatInvoiceForm &&
							previewInvoice.country === "VN" &&
							approvalStatus === approvedStatus
						) {
							handleRecordPaymentVN(vatInvoiceVN);
						}
					}
				});
			} catch (error) {
				console.log("error :", error);
				// Show failed notification
				Noti.create({
					type: "e",
					msg: "Data update failed..",
				});
			}
		} else {
			// Show error notification
			Noti.create({
				type: "e",
				msg: "Error! Please try again later",
			});
		}
		closeForm();
	};

	/**
	 * Get dept that is to approve invoice based on invoice status
	 * @param {Approval status of invoice} approvalStatus
	 */
	const getApprovalStage = (approvalStatus) => {
		if (approvalStatus === 1) return "O&M";
		else return "Accounting";
	};

	/**
	 * Get department that rejected the invoice
	 * @param {Approval status of invoice} approvalStatus
	 */
	const getRejector = (approvalStatus) => {
		if (approvalStatus === -1) return "O&M";
		else return "Accounting";
	};

	/**
	 * Show under-review status of invoice based on user's permission
	 * @param {Selected site} site
	 */
	const getUnderRevCol = (site) => {
		const approvalStage = getApprovalStage(site.approvalStatus);

		return (
			<span
				className={
					approvalStage === "O&M"
						? "status onm-review glyphicon glyphicon-time"
						: "status acc-review glyphicon glyphicon-time"
				}
				title={`Under review (${approvalStage})`}
			></span>
		);
	};

	/**
	 * Show unapproved status of invoice based on user's permission
	 * @param {Selected site} site
	 */
	const getUnapprovedCol = (site) => {
		// Approve and Reject buttons for O&M and Acc
		return (
			<>
				<button
					className="btn btn-primary btn-generate btn-approval"
					title="Approve invoice"
					onClick={() => {
						setPreviewInvoice(site);
						setSelectedSite(formatSelectedSite(site));
						setContext("APPROVE");
						setIsTechReport(false);
						setIsCreditNote(false);
						setFormIsHidden(false);
						setShowGraph("fadeIn");
					}}
				>
					<span className="glyphicon glyphicon-ok"></span>
				</button>
				<button
					title="Reject invoice"
					className="btn btn-danger btn-generate"
					onClick={() => {
						setPreviewInvoice(site);
						setSelectedSite(formatSelectedSite(site));
						setContext("REJECT");
						setIsTechReport(false);
						setIsCreditNote(false);
						setFormIsHidden(false);
						setShowGraph("fadeIn");
					}}
				>
					<span className="glyphicon glyphicon-remove"></span>
				</button>
			</>
		);
	};

	/**
	 * Show rejected status of invoice based on user's permission
	 * @param {Selected site} site
	 * @param {Approval permission of user} approvalPermissions
	 */
	const getRejectedCol = (site, approvalPermissions) => {
		const rejector = getRejector(site.approvalStatus);
		const rejectedByMsg = `by ${rejector}`;

		return (
			<>
				<span
					className="status rejected glyphicon glyphicon-remove-sign"
					onMouseEnter={(e) => {
						const x = e.clientX;
						const y = e.clientY;

						setShowRemark({ ...site, rejectedByMsg });
						setPosition({ x, y });
					}}
					onMouseLeave={() => {
						setShowRemark(null);
						setPosition(null);
					}}
					onClick={() => {
						if (approvalPermissions.level === "IT") {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setContext("RESOLVE");
							setIsTechReport(false);
							setIsCreditNote(false);
							setFormIsHidden(false);
							setShowGraph("fadeIn");
						}
					}}
				></span>
			</>
		);
	};

	/**
	 * Show approved status if invoices are approved completely
	 */
	const getApprovedCol = (site, isApproved = false) => (
		<>
			{/* <button
				className="btn btn-primary btn-generate btn-approval"
				title={site?.invoiceLog && isITUser ? "" : "Update amounts"}
				style={site?.invoiceLog && isITUser ? { display: "none" } : {}} // hide button if invoiceLog exists
				onClick={() => {
					updatePaymentRecord(site);
				}}
			>
				<span className="glyphicon glyphicon-edit"></span>
			</button> */}
			<p>
				<span
					className="status approved glyphicon glyphicon-ok-sign"
					title="Approved"
				></span>
			</p>
		</>
	);

	// Show colorful MSA siteName
	const generateSiteName = (site) => {
		return site.operation_status === 2 ? (
			<p
				style={{ color: "#ff8500", fontSize: "inherit", fontFamily: "inherit" }}
			>
				{site.siteName + ` (MSA)`}
			</p>
		) : (
			<p style={{ color: "#666", fontSize: "inherit", fontFamily: "inherit" }}>
				{site.siteName}
			</p>
		);
	};

	/**
	 * Show generate buttons based on roles
	 */
	const getGenerateCol = (site) => {
		// Show invoice btn role
		const showInvoiceBtn = user.roles.includes(
			"administration:InvoiceTable:_viewInvoiceBtn",
		);
		// Show report btn role
		const showReportBtn = user.roles.includes(
			"administration:InvoiceTable:_viewReportBtn",
		);
		//  Show internal invoice btn role
		const showInternalBtn = user.roles.includes(
			"administration:InvoiceTable:_viewInternalBtn",
		);
		const showExtralineBtn =
			site.approvalStatus < 3 &&
			(site.country === "VN" || site.operation_status === 2) &&
			!(site.special_discount && site.special_discount_comment);
		const showEditBtn =
			!!site.special_discount &&
			!!site.special_discount_comment &&
			site.approvalStatus < 3;

		return (
			<div style={{ width: "fit-content", margin: "auto" }}>
				{showInvoiceBtn && (
					<button
						className="btn btn-generate btn-invoice"
						title="Invoice"
						onClick={() => {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setIsTechReport(false);
							setIsCreditNote(false);
							setPreviewIsHidden(false);
							setShowGraph("fadeIn");
						}}
					>
						<span className="glyphicon glyphicon-btc"></span>
					</button>
				)}
				{showReportBtn && (
					<button
						className="btn btn-generate btn-report"
						title="Client Report"
						onClick={() => {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setIsTechReport(true);
							setIsCreditNote(false);
							setPreviewIsHidden(false);
							setShowGraph("fadeIn");
						}}
					>
						<span className="glyphicon glyphicon-list-alt"></span>
					</button>
				)}

				{(site.client_id === 272 || site.client_id === 209) &&
					site.approvalStatus === 1 && (
						<button
							className="btn btn-generate btn-report"
							title="Change Invoice Date"
							onClick={() => updateInvoiceDate(site.id)}
						>
							<span className="glyphicon glyphicon-calendar"></span>
						</button>
					)}

				{/* Grid Energy Reinjection Button */}
				{site.country === "TH" &&
					(site.operation_status === 3 || site.operation_status === 2) &&
					user.roles.includes(
						"administration:InvoiceTable:_approveInvoice-TH",
					) && (
						<button
							className={`btn btn-generate btn-grid-energy-reinjection ${
								site.is_grid_reinject_applied === 1
									? "btn-grid-applied"
									: "btn-grid-not-applied"
							}`}
							title={
								site.is_grid_reinject_applied === 1
									? "Grid energy reinjection applied - Click to remove"
									: "Grid energy reinjection not applied - Click to apply"
							}
							disabled={site.approvalStatus !== 1}
							onClick={() => handleGridEnergyReinjection(site)}
						>
							<span className={`glyphicon glyphicon-flash`}></span>
						</button>
					)}

				{showExtralineBtn && (
					<button
						className="btn btn-generate btn-plus"
						title="Extra Line"
						onClick={() => {
							setShowInputForm(true);
							setSelectedSite(formatSelectedSite(site));
						}}
					>
						<span className="glyphicon glyphicon-plus"></span>
					</button>
				)}
				{showEditBtn && (
					<button
						className="btn btn-generate btn-pencil"
						title="Edit Line"
						onClick={() => {
							setShowInputForm(true);
							setSelectedSite(formatSelectedSite(site));
						}}
					>
						<span className="glyphicon glyphicon-pencil"></span>
					</button>
				)}
				{showInternalBtn && (
					<button
						className="btn btn-generate btn-internal"
						title="Internal Report"
						onClick={() => alert("Internal report - COMING SOON")}
					>
						<span className="glyphicon glyphicon-scale"></span>
					</button>
				)}
				{showCreditNote && isCreditNoteSite(site) && (
					<button
						className="btn btn-generate btn-credit-note"
						title="Credit Note"
						onClick={() => {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setIsTechReport(false);
							setIsCreditNote(true);
							setPreviewIsHidden(false);
							setShowGraph("fadeIn");
						}}
					>
						<i class="glyphicon glyphicon-credit-card"></i>
					</button>
				)}
				{country === "TH" &&
					site.approvalStatus === 3 &&
					isAbleToSendEmailTH(roles) && (
						<button
							className={`btn btn-primary btn-generate`}
							title={site.emailLog ? "E-invoice was sent" : "Send e-invoice"}
							disabled={site.emailLog}
							onClick={() => {
								findEmailToSend(site);
							}}
						>
							<span className="glyphicon glyphicon-envelope"></span>
						</button>
					)}
			</div>
		);
	};

	/**
	 * Show Status icons based on respective approval permission
	 */
	const getStatusColumn = (site) => {
		const statusStr = site.approvalStatus.toString();
		const roles = user.roles;

		const hasTHApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-TH",
		);

		const hasVNApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-VN",
		);

		const hasPHApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-PH",
		);

		let showApproval = false;
		const hasSameCountry = user.country === site.country;

		switch (site.country) {
			case "TH":
				if (hasTHApprovalRole) showApproval = true;
				break;
			case "VN":
				if (hasVNApprovalRole) showApproval = true;
				break;
			case "PH":
				if (hasPHApprovalRole) showApproval = true;
				break;

			default:
				break;
		}
		// Approved status
		const isApproved = site.approvalStatus === 3;
		if (isApproved) return getApprovedCol(site, true);
		// Rejected invoice status
		const REJcodeArr = approvalPermissions.REJcode.split(",");
		const isRejected = REJcodeArr.includes(statusStr);
		if (isRejected) return getRejectedCol(site, approvalPermissions);

		// Approve and Reject buttons for O&M and Acc
		const UNcodeArr = approvalPermissions.UNcode.split(",");
		const isUnapproved = UNcodeArr.includes(statusStr);
		if (isUnapproved && showApproval) return getUnapprovedCol(site);

		// Under review status
		const REVcodeArr = approvalPermissions.REVcode.split(",");
		const isUnderReview = REVcodeArr.includes(statusStr);
		if (isUnderReview || !hasSameCountry) return getUnderRevCol(site);
	};

	/**
	 * Handle and format invoices sites
	 */
	const handleInvoiceSites = (invoiceSites) => {
		const email_logs = [];

		if (isArray(existingEmailLog)) {
			// take only the distinct email logs
			const distinctEmailLogs = existingEmailLog.filter(
				(v, i, a) => a.findIndex((t) => t.site_id === v.site_id) === i,
			);
			email_logs.push(...distinctEmailLogs);
		}

		// Formatted sites
		const formattedSites =
			invoiceSites != null
				? invoiceSites.map((site) => {
						const prod = {
							solarON: site.solar_ON,
							solarOFF: site.solar_OFF,
							solarTotal: site.solar_total,
							contractID: site.contract_id,
						};
						const EVNInputs = site.EVNInputs;

						const prodObj = calc.calculateProd(prod, EVNInputs);

						return {
							...site,
							clientName: `${site.company} ${getPhase(site.site_code)}`,
							siteName: site.site_name,
							contractDesc: site.contract_desc,
							contractID: site.contract_id,
							displayON: formatNumber(prodObj.solarON, 3),
							displayOFF: formatNumber(prodObj.solarOFF, 3),
							displayTotal: formatNumber(prodObj.solarTotal, 3),
						};
					})
				: [];

		// adding email logs to the formatted sites
		if (email_logs.length > 0 && formattedSites.length > 0) {
			formattedSites.forEach((site) => {
				const emailLog = email_logs.find((log) => log.site_id == site.site_id);
				if (emailLog) {
					site.emailLog = true;
				} else {
					site.emailLog = false;
				}
			});
		}

		const contractIDs = formattedSites.map((site) => site.contractID);
		const distinctIDs = [...new Set(contractIDs)];

		const hasMultipleContracts = distinctIDs.length > 1 ? true : false;

		// Columns of table
		let titles = [
			{
				title: "Operation Status",
				field: "operation_status",
				headerStyle: { display: "none", width: "0px", maxWidth: "0px" },
				cellStyle: { display: "none", width: "0px", maxWidth: "0px" },
				lookup: { 1: "LIP", 2: "MSA", 3: "COD", 4: "TEST" },
			},
			{ title: "Entity", field: "entity_id", width: 100 },
			{ title: "Client", field: "clientName", width: 170 },
			{
				title: "Site Name",
				field: "siteName",
				grouping: false,
				width: 270,
				render: (site) => generateSiteName(site),
			},
			{
				title: `Prod ${peakLabels[0]} (kWh)`,
				field: "displayON",
				grouping: false,
				width: 120,
			},
			{
				title: `Prod ${peakLabels[1]} (kWh)`,
				field: "displayOFF",
				grouping: false,
				width: 120,
			},
			{
				title: "Total Solar Prod (kWh)",
				field: "displayTotal",
				grouping: false,
				width: 120,
			},
			{
				title: "SF ID",
				field: "SF_project_id",
				grouping: false,
				width: 120,
				headerStyle: { display: "none", width: "0px", maxWidth: "0px" },
				cellStyle: { display: "none", width: "0px", maxWidth: "0px" },
			},
			{
				title: "Generate",
				grouping: false,
				render: (site) => getGenerateCol(site),
			},
		];

		// Country has many contract types
		if (hasMultipleContracts)
			titles.unshift({ title: "Contract", field: "contractDesc" });

		// Show Status column only if the user has approval permission
		// and approval is not expired
		const showStatusColumn = approvalPermissions;

		// Status column config
		const statusColumn = {
			title: "Status",
			field: "approvalStatus",
			grouping: false,
			width: 50,
			render: (site) => getStatusColumn(site),
		};

		// Add status column to first in columns array
		if (showStatusColumn) titles.unshift(statusColumn);

		setColumns(titles);
		setInvoiceSites(formattedSites);
	};

	/**
	 * Add a receipt for corresponding invoices
	 * @param {obj} previewInvoice
	 */
	const handleReceipt = (previewInvoice) => {
		const isAMakroSite = isMakroSite(previewInvoice.client_id);
		let invoiceNo = "";

		if (isAMakroSite) {
			invoiceNo = getMakroInvoiceNo(
				previewInvoice.site_code,
				previewInvoice.dateData,
			);
		} else {
			// Validate if invoice(s) are approved
			invoiceNo =
				previewInvoice.invoiceNo != null ? previewInvoice.invoiceNo : "";
		}

		if (invoiceNo !== "") {
			// Fetch invoice(s) of current invoice number
			const invoicesRequest = GYAxios.get(
				`/reports/monthly_calc/invoice_no/${invoiceNo}`,
			);

			invoicesRequest.then((res) => {
				const response = res.data;
				let invoicesAreApproved = false;

				if (response.error == null)
					// Search in array if there are unapproved invoices
					invoicesAreApproved = !response.find(
						(inv) => inv.approvalStatus !== 3,
					);

				if (invoicesAreApproved) {
					try {
						GYAxios.post(`/receipts/insert/${invoiceNo}`).then((res) => {
							if (res.data.error) {
								Noti.create({
									type: "e",
									msg: "Receipt insert failed..",
								});
							}
						});
					} catch (error) {
						console.log("error :", error);

						// Show failed notification
						Noti.create({
							type: "e",
							msg: "Data update failed..",
						});
					}
				}
			});
		}
	};

	/**
	 * Add a EVN input of corresponding invoice
	 * only for SCFIT and FIT sites
	 * @param {obj} invoice
	 */
	const handleEVNInputs = (invoice) => {
		const tableName = invoice.table_name;
		const YYYYMM = moment(invoice.dateData).format("YYYYMM");

		GYAxios.get(`/site/${tableName}/evn_input/month/${YYYYMM}`).then(
			(response) => {
				const EVNInput =
					!response.data.error && response.data.length > 0
						? response.data[0]
						: null;

				if (EVNInput != null) {
					const newStartDate = moment(EVNInput.end_date)
						.add(1, "day")
						.format("YYYY-MM-DD");

					const newRefMonthDate = moment(EVNInput.ref_monthData)
						.add(1, "month")
						.format("YYYY-MM-01");

					const userObj = {
						created_by_ID: user.sub,
						created_by_name: user.name,
						created_at: moment().format("YYYY-MM-DD HH:mm"),
						last_updated_ID: user.sub,
						last_updated_name: user.name,
						last_updated_at: moment().format("YYYY-MM-DD HH:mm"),
					};

					const param = {
						table_name: tableName,
						price_id: EVNInput.latest_price_id,
						start_date: newStartDate,
						ref_monthData: newRefMonthDate,
						...userObj,
					};

					GYAxios.post("/evn_input/new", param).then((res) => {
						console.log("Insert result :", res.data);
					});
				}
			},
		);
	};

	/**
	 * Close preview of invoice
	 */
	const closePreview = () => {
		setPreviewInvoice(null);
		setSelectedSite(null);
		setShowGraph("fadeOut");
		setTimeout(() => {
			setPreviewIsHidden(true);
		}, 300);
	};

	/**
	 * Close approval form component
	 */
	const closeForm = () => {
		setPreviewInvoice(null);
		setSelectedSite(null);
		setShowGraph("fadeOut");
		setTimeout(() => {
			setFormIsHidden(true);
		}, 300);
	};

	const closeConfirm = () => {
		setShowGraph("fadeOut");
		setEmailConfirmShown(false);
	};

	// Sets selected month to state
	// Based on selected month, check if the approval
	// is expired
	useEffect(() => {
		setSelectedMonth(props.selectedMonth);
		const selectedMonth = moment(props.selectedMonth).startOf("month");
		const currentMonth = moment().startOf("month");

		// Considered expired after 1 month from current month
		const isExpired = currentMonth.diff(selectedMonth, "month") > 1;

		if (isExpired) setApprovalIsExpired(true);
		else setApprovalIsExpired(false);

		return () => setApprovalIsExpired(false);
	}, [props.selectedMonth]);

	// Handle sites passed as invoice sites
	useEffect(() => {
		if (props.invoiceSites != null) {
			handleInvoiceSites(props.invoiceSites);
		}

		return () => setInvoiceSites([]);
	}, [props.invoiceSites, peakLabels]);

	// Sets invoices to preview
	const formatSelectedSite = (previewInvoice) => {
		if (previewInvoice != null) {
			const dateStr = selectedMonth.format("YYYY-MM-DD");
			const monthNumber = parseInt(moment(dateStr, "YYYY-MM-DD").format("M"));
			const monthStr = months[monthNumber - 1];

			let monthRange = {};

			switch (true) {
				// Jan - April
				case monthNumber >= 1 && monthNumber <= 4:
					monthRange = quartersMonth[0];
					break;
				// May - August
				case monthNumber >= 5 && monthNumber <= 8:
					monthRange = quartersMonth[1];
					break;
				// September - December
				default:
					monthRange = quartersMonth[2];
					break;
			}

			let docType = {};

			const yearMonth = moment(dateStr, "YYYY-MM-DD").format("YYMM");
			const monthYear = moment(dateStr, "YYYY-MM-DD")
				.endOf("month")
				.format("MMM-YYYY");
			const date = moment(dateStr, "YYYY-MM-DD")
				.endOf("month")
				.format("DD/MM/YYYY");
			const invIssuedDate = previewInvoice.inv_issued_date
				? moment(previewInvoice.inv_issued_date).format("DD/MM/YYYY")
				: date;
			const additional_info = previewInvoice.additional_info
				? JSON.parse(previewInvoice.additional_info)
				: null;
			const isMSA = previewInvoice.operation_status === 2;
			const dueDate = moment(invIssuedDate, "DD/MM/YYYY").add(
				previewInvoice.credit_term,
				"days",
			);

			if (previewInvoice.country === "TH") {
				// Thailand sites
				if (previewInvoice.company !== "Makro") {
					// CP ALL and others
					docType = {
						solarEntity: {
							name: previewInvoice.entity_name,
							taxID: previewInvoice.tax_id,
							entityID: previewInvoice.entity_id,
							address: previewInvoice.entity_address,
							accountantName: previewInvoice.contact_name,
							telephone: previewInvoice.telephone,
							email: previewInvoice.email,
						},
						bankAccount: {
							accName: previewInvoice.acc_name,
							accNumber: previewInvoice.acc_number,
							accNumRemark: previewInvoice.acc_num_remark,
							bankName: previewInvoice.bank_name,
							branch: previewInvoice.branch,
							swiftCode: previewInvoice.swift_code,
						},
						address: previewInvoice.address,
						company: previewInvoice.company,
						invNo: buildInvoiceReceiptNumber({
							type: "INV",
							solarEntityId: previewInvoice.entity_id,
							date: dateStr,
							siteCode: previewInvoice.site_code,
							isMSA,
						}),
						bdNo: "-",
						customerID: previewInvoice.customer_id,
						creditTerm: previewInvoice.credit_term,
						location: previewInvoice.location,
						taxID: previewInvoice.client_tax_id,
						BPAFee: previewInvoice.BPA_fee,
						monthRange,
						operation_status: previewInvoice.operation_status,
						approvalStatus: previewInvoice.approvalStatus,
						msa: previewInvoice.msa,
						msa_day: previewInvoice.msa_day,
						msa_description: previewInvoice.msa_description,
						msa_price: additional_info
							? additional_info.msa_price?.unit_price
							: 0,
						unit_price: previewInvoice.unit_price,
						existingEmailLog: additional_info?.existingEmailLog
							? additional_info.existingEmailLog
							: "",
					};
				} else {
					// Makro Billing Details
					let phase = "";
					const siteCode = previewInvoice.site_code;

					// Phase 2 and future phases of Makro
					if (siteCode.length > 5) phase = siteCode.substring(3, 4);

					docType = {
						company: previewInvoice.company,
						invNo: buildInvoiceReceiptNumber({
							type: "INV",
							solarEntityId: previewInvoice.entity_id,
							siteCode: `MKO${phase}`,
							date: dateStr,
							isMSA,
						}),
						bdNo: buildInvoiceReceiptNumber({
							type: "BD",
							solarEntityId: previewInvoice.entity_id,
							siteCode: `MKO${phase}`,
							date: dateStr,
							isMSA,
						}),
						siteName: previewInvoice.site_name,
						monthRange,
						operation_status: previewInvoice.operation_status,
						solarEntity: {
							name: previewInvoice.entity_name,
							taxID: previewInvoice.tax_id,
							entityID: previewInvoice.entity_id,
							address: previewInvoice.entity_address,
							accountantName: previewInvoice.contact_name,
							telephone: previewInvoice.telephone,
							email: previewInvoice.email,
						},
					};
				}
			} else {
				// Vietnam and Philippines
				docType = {
					solarEntity: {
						name: previewInvoice.entity_name,
						taxID: previewInvoice.tax_id,
						entityID: previewInvoice.entity_id,
						address: previewInvoice.entity_address,
						accountantName: previewInvoice.contact_name,
						telephone: previewInvoice.telephone,
						email: previewInvoice.email,
					},
					bankAccount: {
						accName: previewInvoice.acc_name,
						accNumber: previewInvoice.acc_number,
						accNumRemark: previewInvoice.acc_num_remark,
						bankName: previewInvoice.bank_name,
						branch: previewInvoice.branch,
						swiftCode: previewInvoice.swift_code,
					},
					company: previewInvoice.company,
					invNo: buildInvoiceReceiptNumber({
						type: "INV",
						solarEntityId: previewInvoice.entity_id,
						date: dateStr,
						siteCode: previewInvoice.site_code,
						isMSA,
					}),
					customerID: previewInvoice.customer_id,
					creditTerm: previewInvoice.credit_term,
					location: previewInvoice.location,
					taxID: previewInvoice.client_tax_id,
					contractID: previewInvoice.contract_id,
					projectID: previewInvoice.site_code,
					operation_status: previewInvoice.operation_status,
				};
			}

			const formattedData = {
				...docType,
				tariffData: {
					EAtariff: previewInvoice.EA_id,
					tariffID: previewInvoice.tariff_id,
					tariffDesc: previewInvoice.tariff_desc,
					EAEng: previewInvoice.name_eng,
					EAThai: previewInvoice.name_alt_lang,
				},
				clientID: previewInvoice.client_id,
				billingName: previewInvoice.billing_name,
				billingAddress: previewInvoice.billing_address,
				address: previewInvoice.address,
				priceON: previewInvoice.price_on,
				priceOFF: previewInvoice.price_off,
				FTcost: previewInvoice.FT_cost,
				discountON: previewInvoice.ON_discount,
				discountOFF: previewInvoice.OFF_discount,
				discountFT: previewInvoice.FT_discount,
				specialDiscount: previewInvoice.special_discount,
				specialComment: previewInvoice.special_discount_comment,
				tariffType: previewInvoice.tariff_type,
				EAtariff: previewInvoice.EA_tariff,
				solarON: previewInvoice.solar_ON,
				solarOFF: previewInvoice.solar_OFF,
				solarTotal: previewInvoice.solar_total,
				deliveredTotal: previewInvoice.delivered_total,
				deliveredOn: previewInvoice.delivered_ON,
				deliveredOff: previewInvoice.delivered_OFF,
				isGridReinjectApplied:
					previewInvoice.is_grid_reinject_applied === 1 ? true : false,
				invTopRightNote: previewInvoice.inv_tr_key_val_note
					? JSON.parse(previewInvoice.inv_tr_key_val_note)
					: null,
				dateStr: monthYear,
				date,
				dueDate: dueDate.format("DD/MM/YYYY"),
				selectedMonth,
				monthStr,
				invIssuedDate,
				siteName: previewInvoice.site_name,
				siteCode: previewInvoice.site_code,
				solo: previewInvoice.solo === 1 ? true : false,
				country: previewInvoice.country,
				cod: previewInvoice.cod,
				power: previewInvoice.power,
				city: previewInvoice.city,
				solarModuleModel: previewInvoice.solar_modules_model,
				noSolarModules: previewInvoice.nb_solar_modules,
				siteID: previewInvoice.site_id,
				currency: previewInvoice.currency,
				vat: previewInvoice.vat,
				isEDMI: previewInvoice.EDMI_meter === 1 ? true : false,
				showUnused: previewInvoice.show_unused === 1 ? true : false,
				additional_info,
				old_price:
					previewInvoice.price_start_date != null
						? moment(previewInvoice.price_start_date).format("DD") != "01" &&
							moment(previewInvoice.price_start_date).format("MMYYYY") ==
								moment(selectedMonth).format("MMYYYY")
						: false,
				unit_price: previewInvoice.unit_price || null,
				customInvTemplate: previewInvoice.custom_inv_template || null,
			};

			const selectedSite = {
				label: previewInvoice.site_name,
				table_name: previewInvoice.table_name,
				value: formattedData,
			};

			return selectedSite;
		}
		return null;
	};

	const preparePDFsToSendEmail = async (site) => {
		let paramSite = { ...site };
		const invoiceNo = !isMakroSite(site.client_id)
			? buildInvoiceReceiptNumber({
					type: "INV",
					solarEntityId: site.entity_id,
					date: site.dateData,
					siteCode: site.site_code,
					isMSA: site.operation_status === 2,
				})
			: getMakroInvoiceNo(site.site_code, site.dateData);

		const freeSites = props.freeSites;
		const isFreeMakroSite = freeSites.includes(paramSite.table_name);
		if (!isFreeMakroSite) paramSite = { ...paramSite, invoice_no: invoiceNo };
		paramSite.invoice_no = invoiceNo;
		docsToDownload.current = [];
		const selectedSite = formatSelectedSite(paramSite);
		if (paramSite.client_id !== makroClientId) {
			// none makro sites
			docsToDownload.current.push({
				selectedSite,
				isTechReport: false,
				invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
				docType: BULK_DOC_TYPES.INVOICE,
			});
			docsToDownload.current.push({
				selectedSite,
				isTechReport: false,
				invoiceCopyType: INVOICE_COPY_TYPES.COPY,
				docType: BULK_DOC_TYPES.INVOICE,
			});
		}
		if (paramSite.client_id === makroClientId) {
			// makro sites
			docsToDownload.current.push({
				selectedSite,
				isTechReport: false,
				invoiceCopyType: INVOICE_COPY_TYPES.ORIGINAL,
				docType: BULK_DOC_TYPES.BILLING_DETAIL,
			});
			docsToDownload.current.push({
				selectedSite,
				isTechReport: false,
				invoiceCopyType: INVOICE_COPY_TYPES.COPY,
				docType: BULK_DOC_TYPES.BILLING_DETAIL,
			});
		}
		docsToDownload.current.push({
			selectedSite,
			isTechReport: true,
			docType: BULK_DOC_TYPES.TECH_REPORT,
		});

		setInvoicePreviewForBulkActions(docsToDownload.current[0]);
		setBulkDownloadProgress({
			text: `Generating documents for sending email...`,
			allCount: docsToDownload.current.length,
			loadedCount: 0,
			startTime: new Date().getTime(),
		});

		setSavedToSend(true);
	};

	const findEmailToSend = async (site) => {
		setEmailsRecipients([]);
		try {
			await GYAxios.get(
				`/invoice/get_site_email_add/site_id/${site.site_id}`,
			).then((res) => {
				if (res.data?.error != null) {
					Noti.create({ type: "e", msg: "Error while getting site contacts!" });
					return;
				} else {
					const foundEmailAdds = [];
					if (res.data.length === 0) {
						Noti.create({ type: "w", msg: "No email found!" });
					} else {
						// example of res.data: ["_acctgroup@alfredo.co.th_,_sermkiat@alfredo.co.th_,_kriengsak@alfredo.co.th_,_thanakorn@alfredo.co.th_"]
						const refinedEmails = res.data.map((emails) => {
							// Split emails by comma
							const emailArr = emails.split(",");
							// Remove the first and last underscore from each email
							const refinedEmailArr = emailArr.map((email) => {
								// Use regex to replace only the first and last underscore
								return email.replace(/^_/, "").replace(/_$/, "");
							});
							return refinedEmailArr;
						});
						foundEmailAdds.push(...refinedEmails);
					}

					setShowGraph("fadeIn");
					setEmailsRecipients(foundEmailAdds);
					setEmailConfirmShown(true);
					emailsSite.current = site;
				}
			});
		} catch (error) {
			console.log("error :", error);
			Noti.create({ type: "e", msg: "Error while getting site contacts!" });
			return;
		}
	};

	useEffect(() => {
		if (typeof emailsRecipients === "string" && emailsSite.current !== null) {
			preparePDFsToSendEmail(emailsSite.current);
		}
	}, [emailsRecipients]);

	return (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row>
						<Col md="6">
							<h2 className="panel-title" id="date">
								Invoice Overview - {selectedMonth.format("MMMM YYYY")}
							</h2>
							<br />
							<div className="month-selector">
								Choose another Month: &nbsp;&nbsp;
								<DatePicker
									minDate={localTimeToPickerTime(minDate)}
									maxDate={localTimeToPickerTime(maxDate)}
									selected={localTimeToPickerTime(selectedMonth.toDate())}
									onChange={(date) => {
										setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setReady(false);
									}}
									dateFormat="MMM yyyy"
									showMonthYearPicker
								/>
							</div>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							<img
								src={`${directory}/images/reload.png`}
								onClick={() => reload()}
								style={{
									height: "35px",
									cursor: "pointer",
								}}
								alt="logo"
							/>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							{isGYUser(roles) && (
								<Select
									value={countries.filter((option) => option.value === country)}
									options={countries}
									onChange={(object) => {
										props.setCountry(object.value);
										setCountry(object.value);
										props.setReady(false);
									}}
									defaultValue={countries.filter(
										(option) => option.value === loggedCountry,
									)}
									styles={selectStyle}
								/>
							)}
							{isGYUser(roles) && country === "VN" && (
								<Select
									value={selectedEntity}
									options={entityOpts}
									onChange={(object) => {
										setSelectedEntity(object);
									}}
									styles={selectStyle}
								/>
							)}
						</Col>
					</Row>
					{isShownBulkActions && !!selectedRows.length && (
						<div style={{ marginTop: "1em" }}>
							<div className="invoice-bulk-actions-acct-th">
								{isShownCombinedDownload && (
									<button
										className="btn btn-primary"
										onClick={() => onBulkDownloadClick(BULK_ACTIONS.COMBINED)}
										disabled={isBulkDownloading}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.COMBINED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction === BULK_ACTIONS.COMBINED &&
											bulkDownloadProgress?.text) ||
											"Combined Download"}
										<Popover
											content={
												<div>
													This will combine:
													<ul>
														<li>Original Invoice / Billing Detail</li>
														<li>Copy Invoice / Billing Detail</li>
														<li>O&M Report</li>
														{(isGYUser(roles) ||
															isMakroClientUser(userInfo)) && (
															<>
																<li>
																	Original Makro Invoice (if all Makro sites
																	selected)
																</li>
																<li>
																	Copy Makro Invoice (if all makro sites
																	selected)
																</li>
															</>
														)}
													</ul>
													of selected sites in 1 PDF
													<br />
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
								{isShownSeparateDownload && (
									<button
										className="btn btn-primary"
										onClick={() => onBulkDownloadClick(BULK_ACTIONS.SEPARATED)}
										disabled={isBulkDownloading}
										style={{ marginLeft: "1em" }}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.SEPARATED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.SEPARATED &&
											bulkDownloadProgress?.text) ||
											"Separated Download"}
										<Popover
											content={
												<div>
													This will download a ZIP file containing:
													<ul>
														<li>
															PDFs of Original Invoice / Billing Detail for each
															site selected (1 PDF / 1 site)
														</li>
														<li>
															PDFs of Copy Invoice / Billing Detail for each
															site selected (1 PDF / 1 site)
														</li>
														<li>
															PDFs of O&M Report for each site selected (1 PDF /
															1 site)
														</li>
														{(isGYUser(roles) ||
															isMakroClientUser(userInfo)) && (
															<>
																<li>
																	PDFs of Original Makro Invoices if all Makro
																	sites selected (1 PDF / Makro phase)
																</li>
																<li>
																	PDFs of Copy Makro Invoices if all Makro sites
																	selected (1 PDF / Makro phase)
																</li>
															</>
														)}
														<li>
															1 PDF of Copy Invoices / Billing Details{" "}
															{(isGYUser(roles) ||
																isMakroClientUser(userInfo)) &&
																"and Makro Invoices"}
														</li>
													</ul>
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
								{isShownClientGroupedDownload && (
									<button
										className="btn btn-primary"
										onClick={() =>
											onBulkDownloadClick(BULK_ACTIONS.CLIENT_GROUPED)
										}
										disabled={isBulkDownloading}
										style={{ marginLeft: "1em" }}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.CLIENT_GROUPED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.CLIENT_GROUPED &&
											bulkDownloadProgress?.text) ||
											"Client Grouped Download"}
										<Popover
											content={
												<div>
													This will download a ZIP file containing:
													<ul>
														<li>
															PDFs of Proforma Invoice for each site selected (1
															PDF / 1 site)
														</li>
														<li>
															PDFs of O&M Report for each site selected (1 PDF /
															1 site)
														</li>
													</ul>
													{isGYUser(roles) && (
														<div>
															All PDFs are grouped by client in a folder for
															that client
														</div>
													)}
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
							</div>
						</div>
					)}
				</div>
				<div className="panel-body">
					<MaterialTable
						tableRef={matTbRef}
						columns={columns}
						data={invoicesFilter(invoiceSites)}
						isLoading={!props.ready}
						onSelectionChange={(rows) => setSelectedRows(rows)}
						localization={{
							body: {
								emptyDataSourceMessage: (
									<p style={{ fontSize: "1.5rem" }}>
										Invoices are still under review
									</p>
								),
							},
							grouping: {
								placeholder: "Drag 'Client' header to group by clients",
							},
						}}
						options={{
							showTitle: false,
							selection: isShownBulkActions,
							grouping: true,
							pageSize: 10,
							pageSizeOptions: [10, 20, 40, 60],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
						}}
					/>
				</div>
			</div>
			{position != null && showRemark != null && (
				<div
					className="reject-remark"
					style={{ top: position.y + 20, left: 0 }}
				>
					<p>
						<b>
							Rejected{" "}
							{approvalPermissions.level === "IT" && showRemark.rejectedByMsg} :
						</b>{" "}
						<i>
							{showRemark.rejectedRemark != null
								? showRemark.rejectedRemark
								: "-"}
						</i>
					</p>
				</div>
			)}
			{/* Invoice preview */}
			{!previewIsHidden && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{ textAlign: "center", marginTop: "25px" }}
						className={showGraph}
					>
						{selectedSite != null ? (
							<InvoicePreview
								isTechReport={isTechReport}
								selectedSite={selectedSite}
								isCreditNote={isCreditNote}
								getSiteData={getSiteData}
								calculateForChangingTariff={calculateForChangingTariff}
							/>
						) : (
							<LoaderGraph style={{ height: "72vh" }} />
						)}
						<Button variant="light" onClick={closePreview} className="closeBtn">
							Close
						</Button>
					</div>
				</div>
			)}
			{/* Extra line insert pop up */}
			{showInputForm ? (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{
							marginTop: "25px",
							maxHeight: "85vh",
							overflowY: "auto",
						}}
						className="fadeIn"
					>
						<ExtraLineForm
							ready={props.setReady}
							closeForm={() => setShowInputForm(false)}
							site={selectedSite}
							month={selectedMonth}
						/>
					</div>
				</div>
			) : null}
			{/* Approval form preview */}
			{!formIsHidden && (
				<div className="overlay translucent">
					<div id="form-container" className={showGraph}>
						{selectedSite != null ? (
							<ApprovalContent
								closeForm={closeForm}
								updateInvoiceStatus={updateInvoiceStatus}
								selectedSite={selectedSite}
								showVatInvoiceForm={showVatInvoiceForm}
								process={process}
							/>
						) : (
							<LoaderGraph style={{ height: "72vh" }} />
						)}
					</div>
				</div>
			)}
			{emailConfirmShown && (
				<div className="overlay translucent">
					<div id="form-container" className={showGraph}>
						{emailsRecipients.length >= 0 && (
							<EmailConfirm
								closeForm={closeConfirm}
								recipients={emailsRecipients}
								setNewRecipients={setEmailsRecipients}
								reFetchTable={props.setReady}
								saveNewRecipients={setReSaveRecipients}
							/>
						)}
					</div>
				</div>
			)}
			{/* For generating PDFs for Bulk actions only */}
			{invoicePreviewForBulkActions.selectedSite && (
				<div style={{ display: "none" }}>
					<InvoicePreview
						selectedSite={invoicePreviewForBulkActions.selectedSite}
						isTechReport={invoicePreviewForBulkActions.isTechReport}
						onDocDefinition={onDocDefinitionForBulkActions}
						invoiceCopyType={invoicePreviewForBulkActions.invoiceCopyType}
						docType={invoicePreviewForBulkActions.docType}
						getSiteData={getSiteData}
						calculateForChangingTariff={calculateForChangingTariff}
					/>
				</div>
			)}
			{makroInvoicePreviewForBulkActions.makroSites && (
				<div style={{ display: "none" }}>
					<SummaryInvoiceTemplate
						summaryInvoiceSites={makroInvoicePreviewForBulkActions.makroSites}
						bankAccount={makroInvoicePreviewForBulkActions.bankAccount}
						selectedMonth={selectedMonth}
						selectedPhase={makroInvoicePreviewForBulkActions.selectedPhase}
						onDocDefinition={onDocDefinitionForBulkActions}
						invoiceCopyType={makroInvoicePreviewForBulkActions.invoiceCopyType}
						docType={makroInvoicePreviewForBulkActions.docType}
					/>
				</div>
			)}
			{CFWInvoicePreviewForBulkActions.cfwSites && (
				<div style={{ display: "none" }}>
					<SummaryInvoiceCFW
						summaryInvoiceSites={CFWInvoicePreviewForBulkActions.cfwSites}
						bankAccount={CFWInvoicePreviewForBulkActions.bankAccount}
						selectedMonth={selectedMonth}
						selectedPhase={CFWInvoicePreviewForBulkActions.selectedPhase}
						onDocDefinition={onDocDefinitionForBulkActions}
						invoiceCopyType={CFWInvoicePreviewForBulkActions.invoiceCopyType}
						docType={CFWInvoicePreviewForBulkActions.docType}
					/>
				</div>
			)}
			{/* For bulk download loading overlay */}
			{bulkDownloadProgress && (
				<div className="loading-overlay">
					<div className="spinner"></div>
					<div className="text">{bulkDownloadProgress.text}</div>
					{bulkDownloadProgressWarning && (
						<div
							className="text"
							style={{ color: "#d7ff46", fontWeight: "bold" }}
						>
							{bulkDownloadProgressWarning}
						</div>
					)}
				</div>
			)}
		</>
	);
}

export default InvoiceTable;
